// ─── Invitation Editor ────────────────────────────────────────
// Long scroll editor with phone preview.
// Left: section fields in a scrollable list.
// Right: live phone mockup rendering TemplateRenderer.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Eye, Upload, Trash2, Plus, ChevronDown, ChevronUp, ExternalLink, Image as ImageIcon, GripVertical, Layers, Link2, Check, Undo2, Redo2, Palette, SlidersHorizontal, Paintbrush, Clock, Grid3X3, MessageSquare, MapPin, Play, Sparkles, Globe } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import TemplateRenderer from '../components/renderer/TemplateRenderer';
import { elegantWeddingV1 } from '../lib/templates/elegant-wedding-v1';
import { noirVinylV1 } from '../lib/templates/noir-vinyl-v1';
import { timelessEleganceV1 } from '../lib/templates/timeless-elegance-v1';
import { modernMinimalV1 } from '../lib/templates/modern-minimal-v1';
import { editorialRomanceV1 } from '../lib/templates/editorial-romance-v1';
import { eveningLuxeV1 } from '../lib/templates/evening-luxe-v1';
import { memoryCollageV1 } from '../lib/templates/memory-collage-v1';
import { oceanEditorialCollageV1 } from '../lib/templates/ocean-editorial-collage-v1';
import { universalStarterV1 } from '../lib/templates/universal-starter-v1';
import { freeformStudioV1 } from '../lib/templates/freeform-studio-v1';
import CollageEditor from '../components/collage/CollageEditor';
import LayerPanel from '../components/collage/LayerPanel';
import LayerOrderControls from '../components/collage/LayerOrderControls';
import StyleSwapBar from '../components/collage/StyleSwapBar';
import SectionManager, { SECTION_DEFAULTS } from '../components/editor/SectionManager';
import MotionPanel from '../components/editor/MotionPanel';
import ElementStylePanel from '../components/editor/ElementStylePanel';
import AlignmentToolbar from '../components/editor/AlignmentToolbar';
import WidgetSectionEditor from '../components/editor/WidgetSectionEditor';
import StudioToolbar from '../components/editor/StudioToolbar';
import SaveAsTemplateModal from '../components/editor/SaveAsTemplateModal';
import LanguagesPanel from '../components/editor/LanguagesPanel';
import SnapshotsPanel from '../components/editor/SnapshotsPanel';
import MultiSelectionBar from '../components/editor/MultiSelectionBar';
import RenderParityBanner from '../components/editor/RenderParityBanner';
import NewVersionBanner from '../components/editor/NewVersionBanner';
import { buildPublicPreviewUrl } from '../components/editor/PhonePreviewModal';
import { snapshotCanvas, snapshotPublicRender, compareSnapshots } from '../lib/utils/renderParityGuard';
import { IMAGE_ROLES } from '../lib/utils/imageRoles';
import { computePhotoRowOverrides } from '../lib/utils/photoRowLayout';
import { CollageEditContext } from '../components/collage/CollageEditContext';
import { useContentHistory } from '../hooks/useContentHistory';
import { palettes } from '../lib/tokens/palettes';
import { fontPairs } from '../lib/tokens/typography';
import { autoBalanceLayout, calculateVisualCenter } from '../lib/utils/autoBalanceLayout';
import { OpenerDraftProvider } from '../components/studio/openers/OpenerDraftContext';
import OpenerEditorPanel from '../components/studio/openers/OpenerEditorPanel';
// Phase 1.9.19 — Entry Cover panel is loaded lazily so the custom
// babel-metadata plugin doesn't recurse through its binding tree from
// inside this 3000+ line file (it was overflowing the V8 stack while
// statically resolving the JSX chain). React.lazy() turns the import
// into an async function call that the plugin can't follow eagerly.
const EntryCoverPanelHost = React.lazy(() => import('../components/studio/entry/EntryCoverPanelHost'));
import OpenerPreviewOverlay from '../components/studio/openers/OpenerPreviewOverlay';

const API = process.env.REACT_APP_BACKEND_URL;
const TEMPLATES = {
  'elegant-wedding-v1': elegantWeddingV1,
  'noir-vinyl-v1': noirVinylV1,
  'timeless-elegance-v1': timelessEleganceV1,
  'modern-minimal-v1': modernMinimalV1,
  'editorial-romance-v1': editorialRomanceV1,
  'evening-luxe-v1': eveningLuxeV1,
  'memory-collage-v1': memoryCollageV1,
  'ocean-editorial-collage-v1': oceanEditorialCollageV1,
  'universal-starter-v1': universalStarterV1,
  'freeform-studio-v1': freeformStudioV1,
};

// ─── Save Status Indicator ────────────────────────────────────
// Tiny status pill next to the Save Draft button in the header.
// Shows: "Auto-saving…" / "Unsaved" / "Saved · just now" / "Saved · 3m ago"
function SaveStatusIndicator({ dirty, autoSaving, saving, lastSavedAt }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  let label = '';
  let dotClass = '';
  let testid = 'save-status-idle';
  if (saving || autoSaving) {
    label = autoSaving ? 'Auto-saving…' : 'Saving…';
    dotClass = 'bg-blue-500 animate-pulse';
    testid = 'save-status-saving';
  } else if (dirty) {
    label = 'Unsaved';
    dotClass = 'bg-amber-500';
    testid = 'save-status-dirty';
  } else if (lastSavedAt) {
    const diffMs = now - lastSavedAt;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMs < 30000) label = 'Saved · just now';
    else if (diffMin < 1) label = 'Saved · moments ago';
    else if (diffMin < 60) label = `Saved · ${diffMin}m ago`;
    else label = `Saved · ${Math.floor(diffMin / 60)}h ago`;
    dotClass = 'bg-emerald-500';
    testid = 'save-status-saved';
  } else {
    return null;
  }

  return (
    <div className="flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-medium text-gray-500 dark:text-gray-400" data-testid={testid}>
      <span className={`w-1.5 h-1.5 rounded-full ${dotClass}`} />
      <span>{label}</span>
    </div>
  );
}

// ─── Iframe Preview Bridge ────────────────────────────────────
// Posts data to the /editor-preview iframe for correct mobile viewport rendering.
function IframePreview({ templateConfig, sections, invitationData, themeOverrides, slug, selectedElement }) {
  const iframeRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const TRUSTED_ORIGIN = window.location.origin;
    const handleMessage = (e) => {
      if (e.origin !== TRUSTED_ORIGIN) return;
      if (e.data?.type === 'EDITOR_PREVIEW_READY') setReady(true);
      if (e.data?.type === 'EDITOR_PREVIEW_SELECT') {
        // Forward selection from iframe to parent (for collage interaction)
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;
    const timer = setTimeout(() => {
      try {
        iframe.contentWindow.postMessage({
          type: 'EDITOR_PREVIEW_UPDATE',
          payload: {
            templateConfig: { ...templateConfig, sections },
            invitationData,
            themeOverrides,
            slug,
            isCollage: !!templateConfig?.isCollage,
            selectedElement,
          },
        }, window.location.origin);
      } catch (e) { /* iframe not ready yet */ }
    }, ready ? 0 : 500);
    return () => clearTimeout(timer);
  }, [templateConfig, sections, invitationData, themeOverrides, slug, selectedElement, ready]);

  return (
    <iframe
      ref={iframeRef}
      src="/editor-preview"
      title="Mobile Preview"
      style={{ width: 390, height: 844, border: 'none' }}
      data-testid="preview-iframe"
    />
  );
}

// ─── Variant Registry ─────────────────────────────────────────
const SECTION_VARIANTS = {
  'hero': [
    { id: 'hero-centered-image', label: 'Centered Image' },
    { id: 'hero-split', label: 'Split Layout' },
    { id: 'hero-album-sleeve', label: 'Album Sleeve' },
    { id: 'hero-cinematic', label: 'Cinematic' },
    { id: 'hero-dark-cinematic', label: 'Dark Cinematic' },
  ],
  'story': [
    { id: 'story-single-column', label: 'Single Column' },
    { id: 'story-image-left', label: 'Image + Text' },
  ],
  'event-details': [
    { id: 'details-grid', label: 'Card Grid' },
    { id: 'details-stack', label: 'Stacked List' },
    { id: 'details-elegant-columns', label: 'Elegant Columns' },
  ],
  'countdown': [
    { id: 'countdown-centered', label: 'Centered' },
    { id: 'countdown-luxe', label: 'Luxe Panel' },
  ],
  'timeline': [
    { id: 'timeline-vertical', label: 'Vertical' },
    { id: 'timeline-centered', label: 'Centered' },
  ],
  'gallery': [
    { id: 'gallery-grid', label: 'Grid' },
    { id: 'gallery-masonry', label: 'Masonry' },
    { id: 'gallery-editorial', label: 'Editorial' },
    { id: 'gallery-dark-editorial', label: 'Dark Editorial' },
  ],
  'venue': [
    { id: 'venue-card', label: 'Card' },
    { id: 'venue-split', label: 'Split + Image' },
    { id: 'venue-card-refined', label: 'Refined Card' },
    { id: 'venue-split-luxe', label: 'Luxe Split' },
  ],
  'rsvp': [
    { id: 'rsvp-card', label: 'Card' },
    { id: 'rsvp-minimal', label: 'Minimal' },
    { id: 'rsvp-soft-card', label: 'Soft Card' },
    { id: 'rsvp-luxe-card', label: 'Luxe Card' },
    { id: 'rsvp-detailed', label: 'Detailed' },
    { id: 'rsvp-immersive', label: 'Immersive' },
  ],
  'footer': [
    { id: 'footer-centered', label: 'Centered' },
    { id: 'footer-minimal', label: 'Minimal' },
    { id: 'footer-centered-luxe', label: 'Centered Luxe' },
  ],
};

// ─── Field Components ─────────────────────────────────────────
function TextField({ label, value, onChange, placeholder, multiline }) {
  const cls = "w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all";
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">{label}</label>
      {multiline ? (
        <textarea rows={3} className={cls} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
      ) : (
        <input type="text" className={cls} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
      )}
    </div>
  );
}

function ImageField({ label, value, onUpload, onRemove, uploading }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">{label}</label>
      <div className="flex items-center gap-3">
        <div className="w-16 h-16 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex-shrink-0 overflow-hidden flex items-center justify-center">
          {value ? (
            <img src={value.startsWith('/') ? `${API}${value}` : value} alt="" className="w-full h-full object-cover" />
          ) : (
            <ImageIcon className="w-5 h-5 text-gray-300" />
          )}
        </div>
        <div className="flex gap-2">
          <label className={`px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-colors ${
            uploading ? 'bg-gray-100 text-gray-400' : 'bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400'
          }`}>
            {uploading ? 'Uploading...' : 'Upload'}
            <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={e => {
              const f = e.target.files?.[0];
              if (f) onUpload(f);
              e.target.value = '';
            }} />
          </label>
          {value && (
            <button onClick={onRemove} className="px-2 py-1.5 rounded-lg text-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function TimelineEditor({ items, onChange }) {
  const addItem = () => onChange([...items, { time: '', title: '', description: '' }]);
  const removeItem = (i) => onChange(items.filter((_, idx) => idx !== i));
  const updateItem = (i, field, val) => {
    const updated = [...items];
    updated[i] = { ...updated[i], [field]: val };
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      {items.map((item, i) => (
        <div key={i} className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-500">Item {i + 1}</span>
            <button onClick={() => removeItem(i)} className="text-red-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
          <input className="w-full px-2 py-1.5 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100" placeholder="Time (e.g. 16:00)" value={item.time || ''} onChange={e => updateItem(i, 'time', e.target.value)} />
          <input className="w-full px-2 py-1.5 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100" placeholder="Title" value={item.title || ''} onChange={e => updateItem(i, 'title', e.target.value)} />
          <input className="w-full px-2 py-1.5 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100" placeholder="Description" value={item.description || ''} onChange={e => updateItem(i, 'description', e.target.value)} />
        </div>
      ))}
      <button onClick={addItem} className="w-full py-2 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 text-xs text-gray-500 hover:text-gray-700 hover:border-gray-400 transition-colors flex items-center justify-center gap-1.5">
        <Plus className="w-3.5 h-3.5" /> Add Item
      </button>
    </div>
  );
}

function FAQEditor({ items, onChange }) {
  const addItem = () => onChange([...items, { q: '', a: '' }]);
  const removeItem = (i) => onChange(items.filter((_, idx) => idx !== i));
  const updateItem = (i, field, val) => {
    const updated = [...items];
    updated[i] = { ...updated[i], [field]: val };
    onChange(updated);
  };
  return (
    <div className="space-y-3">
      {items.map((item, i) => (
        <div key={i} className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-500">Q&A {i + 1}</span>
            <button onClick={() => removeItem(i)} className="text-red-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
          <input className="w-full px-2 py-1.5 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100" placeholder="Question" value={item.q || ''} onChange={e => updateItem(i, 'q', e.target.value)} />
          <textarea rows={2} className="w-full px-2 py-1.5 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100" placeholder="Answer" value={item.a || ''} onChange={e => updateItem(i, 'a', e.target.value)} />
        </div>
      ))}
      <button onClick={addItem} className="w-full py-2 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 text-xs text-gray-500 hover:text-gray-700 hover:border-gray-400 transition-colors flex items-center justify-center gap-1.5">
        <Plus className="w-3.5 h-3.5" /> Add Q&A
      </button>
    </div>
  );
}

function GalleryEditor({ images, onUpload, onRemove, uploading }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        {images.map((img, i) => (
          <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 group">
            <img src={img.startsWith('/') ? `${API}${img}` : img} alt="" className="w-full h-full object-cover" />
            <button
              onClick={() => onRemove(i)}
              className="absolute top-1 right-1 p-1 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>
      <label className={`block w-full py-3 rounded-lg border border-dashed text-center cursor-pointer transition-colors ${
        uploading ? 'border-gray-200 text-gray-400' : 'border-gray-300 dark:border-gray-600 text-gray-500 hover:border-gray-400'
      }`}>
        <span className="text-xs flex items-center justify-center gap-1.5">
          <Upload className="w-3.5 h-3.5" /> {uploading ? 'Uploading...' : 'Add Photo'}
        </span>
        <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={e => {
          const f = e.target.files?.[0];
          if (f) onUpload(f);
          e.target.value = '';
        }} />
      </label>
    </div>
  );
}

// ─── Collapsible Panel Wrapper ────────────────────────────────
function CollapsiblePanel({ title, icon: Icon, defaultOpen = true, children, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
        data-testid={testId}
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-3.5 h-3.5 text-gray-400" />}
          <span className="text-[11px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">{title}</span>
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 border-t border-gray-100 dark:border-gray-800 pt-3">
          {children}
        </div>
      )}
    </div>
  );
}

// ─── Studio Page Flow ─────────────────────────────────────────
// Compact visual showing all sections (canvas + widgets) in order.
// Allows drag-reorder so widgets can be placed between canvas segments.
const SECTION_ICONS = { collage: Layers, hero: ImageIcon, timeline: Clock, gallery: Grid3X3, rsvp: MessageSquare, 'event-details': MapPin };
const SECTION_LABELS = { collage: 'Canvas', hero: 'Hero', timeline: 'Timeline', gallery: 'Gallery', rsvp: 'Response Card', 'event-details': 'Event Details' };

function StudioPageFlow({ sections, onReorder, onRemove, onUpdateYPosition }) {
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  const handleDragStart = (e, idx) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDrop = (e, idx) => {
    e.preventDefault();
    if (dragIdx !== null && dragIdx !== idx) {
      onReorder(dragIdx, idx);
    }
    setDragIdx(null);
    setOverIdx(null);
  };

  const visibleSections = sections.filter(s => s.visible);
  if (visibleSections.length <= 1) return null;

  return (
    <div className="p-3 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900" data-testid="studio-page-flow">
      <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
        Page Flow
      </label>
      <div className="space-y-1.5">
        {sections.map((section, idx) => {
          if (!section.visible) return null;
          const SIcon = SECTION_ICONS[section.type] || Layers;
          const label = SECTION_LABELS[section.type] || section.label || section.type;
          const isCanvas = section.type === 'collage';
          const isHero = section.type === 'hero';
          const isDragging = dragIdx === idx;
          const isOver = overIdx === idx && dragIdx !== idx;
          const hasYPosition = !isCanvas && !isHero;

          return (
            <div key={section.id}>
              <div
                draggable
                onDragStart={e => handleDragStart(e, idx)}
                onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (idx !== overIdx) setOverIdx(idx); }}
                onDrop={e => handleDrop(e, idx)}
                onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
                className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs transition-all cursor-grab active:cursor-grabbing ${
                  isDragging ? 'opacity-40' : ''
                } ${isOver ? 'ring-1 ring-blue-400' : ''} ${
                  isCanvas
                    ? 'bg-blue-50 dark:bg-blue-900/15 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800'
                    : 'bg-gray-50 dark:bg-gray-800/50 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700'
                }`}
                data-testid={`page-flow-${section.id}`}
              >
                <GripVertical className="w-3 h-3 flex-shrink-0 opacity-40" />
                <SIcon className="w-3 h-3 flex-shrink-0" />
                <span className="flex-1 font-medium truncate">{label}</span>
                {!isCanvas && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onRemove(section.id); }}
                    className="p-0.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-400 hover:text-red-500 transition-colors"
                    data-testid={`page-flow-remove-${section.id}`}
                  >
                    <Trash2 className="w-2.5 h-2.5" />
                  </button>
                )}
              </div>
              {/* Y-position slider for inline widgets */}
              {hasYPosition && onUpdateYPosition && (
                <div className="flex items-center gap-2 px-2 mt-1 mb-0.5">
                  <span className="text-[9px] text-gray-400 whitespace-nowrap w-12">Position</span>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={1}
                    value={Math.round((section.canvasYPosition || 0.5) * 100)}
                    onChange={e => onUpdateYPosition(section.id, parseInt(e.target.value) / 100)}
                    className="flex-1 accent-purple-500 h-1"
                    data-testid={`page-flow-ypos-${section.id}`}
                  />
                  <span className="text-[9px] text-gray-400 w-8 text-right">{Math.round((section.canvasYPosition || 0.5) * 100)}%</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-[9px] text-gray-400 mt-1.5">Drag to reorder · Slider sets position in canvas</p>
    </div>
  );
}


// ─── Section Editor Panel ─────────────────────────────────────
function SectionPanel({ section, sectionData, theme, onDataChange, onToggle, onVariantChange, onImageUpload, uploadingField, slug, dragHandleProps, allSections }) {
  const [collapsed, setCollapsed] = useState(false);
  const fields = section.editableRules?.fields || [];
  const variants = SECTION_VARIANTS[section.type] || [];

  const sectionLabels = {
    'hero': 'Hero',
    'story': 'Story / Message',
    'countdown': 'Countdown',
    'event-details': 'Event Details',
    'timeline': 'Timeline / Schedule',
    'gallery': 'Gallery',
    'venue': 'Venue / Map',
    'rsvp': 'Response Card',
    'faq': 'FAQ',
    'quote': 'Quote',
    'footer': 'Footer',
  };

  const updateField = (field, val) => {
    onDataChange(section.id, { ...sectionData, [field]: val });
  };

  const renderField = (field) => {
    const val = sectionData[field];
    switch (field) {
      case 'backgroundImage':
      case 'image':
      case 'vinylCoverImage':
        return <ImageField key={field} label={field === 'backgroundImage' ? 'Background Image' : field === 'vinylCoverImage' ? 'Vinyl Cover' : 'Image'} value={val} onUpload={(f) => onImageUpload(section.id, field, f)} onRemove={() => updateField(field, '')} uploading={uploadingField === `${section.id}.${field}`} />;
      case 'paragraph':
      case 'secondaryParagraph':
      case 'description':
      case 'closingMessage':
      case 'note':
      case 'quoteText':
        return <TextField key={field} label={humanize(field)} value={val} onChange={v => updateField(field, v)} multiline />;
      case 'items':
        if (section.type === 'timeline' || section.type === 'event-details') {
          return <TimelineEditor key={field} items={val || []} onChange={v => updateField(field, v)} />;
        }
        if (section.type === 'faq') {
          return <FAQEditor key={field} items={val || []} onChange={v => updateField(field, v)} />;
        }
        return null;
      case 'images':
        return <GalleryEditor key={field} images={val || []} onUpload={(f) => onImageUpload(section.id, field, f)} onRemove={(i) => updateField(field, (val || []).filter((_, idx) => idx !== i))} uploading={uploadingField === `${section.id}.${field}`} />;
      case 'targetDateTime':
        return <TextField key={field} label="Event Date & Time" value={val} onChange={v => updateField(field, v)} placeholder="2026-09-12T16:00:00" />;
      case 'enabledFields':
      case 'captions':
        return null;
      case 'musicUrl':
        return <TextField key={field} label="Music URL" value={val} onChange={v => updateField(field, v)} placeholder="Upload audio separately" />;
      case 'ctaText':
        return <TextField key={field} label="CTA Button Text" value={val} onChange={v => updateField(field, v)} placeholder="Leave empty to hide button" />;
      case 'ctaTarget': {
        const targetOptions = (allSections || [])
          .filter(s => s.id !== section.id && s.visible)
          .map(s => ({ value: s.type, label: sectionLabels[s.type] || s.type }));
        // Dedupe by type
        const seen = new Set();
        const uniqueTargets = targetOptions.filter(t => { if (seen.has(t.value)) return false; seen.add(t.value); return true; });
        return (
          <div key={field}>
            <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">CTA Scroll Target</label>
            <select
              value={val || 'rsvp'}
              onChange={e => updateField(field, e.target.value)}
              className="w-full px-2.5 py-1.5 text-xs rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-200 outline-none focus:ring-1 focus:ring-blue-400"
              data-testid="cta-target-select"
            >
              <option value="rsvp">RSVP</option>
              {uniqueTargets.filter(t => t.value !== 'rsvp').map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            <p className="text-[10px] text-gray-400 mt-1">Where the button scrolls to when clicked</p>
          </div>
        );
      }
      default:
        return <TextField key={field} label={humanize(field)} value={val} onChange={v => updateField(field, v)} />;
    }
  };

  return (
    <div className="border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden bg-white dark:bg-gray-900" data-testid={`section-panel-${section.id}`}>
      <div
        className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
        onClick={() => setCollapsed(c => !c)}
      >
        <div className="flex items-center gap-2">
          {/* Drag handle */}
          <div
            {...dragHandleProps}
            className="cursor-grab active:cursor-grabbing p-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
            onClick={e => e.stopPropagation()}
            data-testid={`drag-handle-${section.id}`}
          >
            <GripVertical className="w-3.5 h-3.5 text-gray-400" />
          </div>
          <div className={`w-2 h-2 rounded-full ${section.visible ? 'bg-green-400' : 'bg-gray-300'}`} />
          <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
            {sectionLabels[section.type] || section.type}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); onToggle(section.id); }}
            className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
              section.visible ? 'bg-green-50 text-green-600 dark:bg-green-900/20' : 'bg-gray-100 text-gray-400 dark:bg-gray-800'
            }`}
          >
            {section.visible ? 'ON' : 'OFF'}
          </button>
          {collapsed ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronUp className="w-4 h-4 text-gray-400" />}
        </div>
      </div>

      {!collapsed && section.visible && (
        <div className="px-4 pb-4 space-y-4 border-t border-gray-100 dark:border-gray-800 pt-4">
          {/* Variant switcher */}
          {variants.length > 1 && (
            <div>
              <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                <Layers className="w-3 h-3" /> Layout Variant
              </label>
              <div className="flex flex-wrap gap-1.5">
                {variants.map(v => (
                  <button
                    key={v.id}
                    onClick={() => onVariantChange(section.id, v.id)}
                    className={`px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all ${
                      section.variant === v.id
                        ? 'bg-blue-500 text-white shadow-sm'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                    data-testid={`variant-btn-${v.id}`}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          {fields.map(renderField)}
        </div>
      )}
    </div>
  );
}

function humanize(str) {
  return str.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).replace(/_/g, ' ');
}

// ─── Draggable Section List ───────────────────────────────────
function DraggableSectionList({ sections, content, templateConfig, onDataChange, onToggle, onVariantChange, onImageUpload, uploadingField, slug, onReorder }) {
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  const handleDragStart = (e, idx) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
    // Transparent drag image
    const el = document.createElement('div');
    el.style.position = 'absolute';
    el.style.top = '-9999px';
    document.body.appendChild(el);
    e.dataTransfer.setDragImage(el, 0, 0);
    setTimeout(() => document.body.removeChild(el), 0);
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (idx !== overIdx) setOverIdx(idx);
  };

  const handleDrop = (e, idx) => {
    e.preventDefault();
    if (dragIdx !== null && dragIdx !== idx) {
      onReorder(dragIdx, idx);
    }
    setDragIdx(null);
    setOverIdx(null);
  };

  const handleDragEnd = () => {
    setDragIdx(null);
    setOverIdx(null);
  };

  return (
    <div className="space-y-3" data-testid="section-list">
      {sections.map((section, idx) => {
        const sectionData = {
          ...(section.data || {}),
          ...(content[section.id] || {}),
        };
        const isDragging = dragIdx === idx;
        const isOver = overIdx === idx && dragIdx !== idx;
        return (
          <div
            key={section.id}
            draggable
            onDragStart={e => handleDragStart(e, idx)}
            onDragOver={e => handleDragOver(e, idx)}
            onDrop={e => handleDrop(e, idx)}
            onDragEnd={handleDragEnd}
            style={{
              opacity: isDragging ? 0.4 : 1,
              transform: isOver ? 'translateY(2px)' : 'none',
              transition: 'opacity 0.15s, transform 0.15s',
            }}
          >
            {isOver && (
              <div className="h-0.5 rounded-full bg-blue-500 mb-2 mx-4" />
            )}
            <SectionPanel
              section={section}
              sectionData={sectionData}
              theme={templateConfig.theme}
              onDataChange={onDataChange}
              onToggle={onToggle}
              onVariantChange={onVariantChange}
              onImageUpload={onImageUpload}
              uploadingField={uploadingField}
              slug={slug}
              dragHandleProps={{}}
              allSections={sections}
            />
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
//
// Phase 2.0.1 — When `editorMode="template"` is passed, this same
// editor runs against a template document (PUT /templates/{id}/content)
// instead of an event's rsvp_page. Event-only UI (publish, share preview,
// review note, etc.) is hidden in that mode. See TemplateEditorPage.js.
//
// Default mode is "event" so /events/:eventId/invitation-editor keeps
// working exactly as before.
//
export default function InvitationEditor({
  editorMode = 'event',
  templateId: templateIdProp,
  initialTemplate,
}) {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const isTemplateMode = editorMode === 'template';
  const templateId = templateIdProp;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templateConfig, setTemplateConfig] = useState(null);
  const [content, setContent, history] = useContentHistory({});
  const [showLayers, setShowLayers] = useState(false);
  const [activeStylePreset, setActiveStylePreset] = useState(null);
  const [themeOverrides, setThemeOverrides] = useState({});
  const [sections, setSections] = useState([]);
  const [slug, setSlug] = useState('');
  const [rsvpPageInfo, setRsvpPageInfo] = useState(null); // Phase 1.7 — QA helpers
  // Phase 1.9 — Cinematic opener (Rain on Cotton Paper).
  // Loaded lazily from /api/events/{id}; saved via PATCH /opener.
  // Lives outside dynamicSections so it never enters the canvas pipeline.
  const [openerConfig, setOpenerConfig] = useState(null);
  // Phase 1.9.19 — Entry Cover (invitation-level "tap to open" overlay).
  // Mirrors opener loading/saving pattern. Strictly additive; never enters
  // the canvas pipeline, element arrays, or layout resolvers.
  const [entryCoverConfig, setEntryCoverConfig] = useState(null);
  const [uploadingField, setUploadingField] = useState(null);
  const [isPublished, setIsPublished] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedShowcase, setCopiedShowcase] = useState(false);
  const previewRef = useRef(null);
  const [selectedElement, setSelectedElement] = useState(null);
  // Phase 1.9 — lightweight multi-select. UI-only state (never persisted).
  // When length >= 2, group nudge & "Convert to photo row" apply.
  const [selectionSet, setSelectionSet] = useState([]);
  const [uploadingGlobalBg, setUploadingGlobalBg] = useState(false);
  const [globalBgMeta, setGlobalBgMeta] = useState(null); // { width, height, profile, sizeBytes } — info pill source
  const [gridEnabled, setGridEnabled] = useState(false);
  // Motion 1.0 — template/page-level motion preset. Defaults to 'none' for
  // every existing event (no auto-migration). Persists via v2_settings_overrides.
  const [motionPreset, setMotionPreset] = useState('none');
  // Phase 1.8 — designer-only "Show guides" toggle, persisted to localStorage.
  const [guidesEnabled, setGuidesEnabled] = useState(() => {
    try { return localStorage.getItem('studio.guides.enabled') === '1'; } catch { return false; }
  });
  const toggleGuides = useCallback(() => {
    setGuidesEnabled(v => {
      const next = !v;
      try { localStorage.setItem('studio.guides.enabled', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  }, []);
  const [animationPreview, setAnimationPreview] = useState(false);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  // Multi-language state
  const [showLanguagesPanel, setShowLanguagesPanel] = useState(false);
  const [eventLanguages, setEventLanguages] = useState([]);
  const [eventDefaultLanguage, setEventDefaultLanguage] = useState('en');
  const [v2Translations, setV2Translations] = useState({});

  // Phase Lang-A.1 — derived: does any enabled non-default language have a
  // stale overlay? Used by both the header Languages button (top-right) and
  // the StudioToolbar Languages button to surface a tiny amber dot.
  const hasStaleLanguage = React.useMemo(() => {
    const contentUpd = rsvpPageInfo?.updated_at || null;
    return (eventLanguages || []).some((code) => {
      if (code === eventDefaultLanguage) return false;
      const overlay = (v2Translations || {})[code];
      if (!overlay) return false;
      const gen = overlay._generated_at;
      if (!gen) return true; // pre-Lang-A.1 overlay → treat as stale once
      if (!contentUpd) return false;
      return new Date(contentUpd) > new Date(gen);
    });
  }, [eventLanguages, eventDefaultLanguage, v2Translations, rsvpPageInfo?.updated_at]);

  // ── Auto-save: track dirty state, debounce 10s save, warn on unload ──
  const [dirty, setDirty] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  // Render parity guard — populated by a best-effort post-save check
  // (see handleSave). Null = no check yet OR all green. Non-null with
  // issues.length > 0 → show the warning banner.
  const [parityResult, setParityResult] = useState(null);
  const loadedRef = useRef(false);
  const autoSaveTimerRef = useRef(null);
  const handleSaveRef = useRef(null);

  // Mark "loaded" 200ms after initial fetch settles, so the load() state-setters
  // don't trigger a false-positive "dirty" cycle.
  useEffect(() => {
    if (!loading && templateConfig && !loadedRef.current) {
      const t = setTimeout(() => { loadedRef.current = true; }, 200);
      return () => clearTimeout(t);
    }
  }, [loading, templateConfig]);

  // Watch for any user edit → mark dirty + debounce a silent auto-save (10s).
  // Uses handleSaveRef so we don't depend on handleSave's identity.
  useEffect(() => {
    if (!loadedRef.current) return;
    setDirty(true);
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      handleSaveRef.current?.({ silent: true });
    }, 10000);
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [content, themeOverrides, sections]);

  // Warn before closing the tab / refreshing if there are unsaved changes.
  useEffect(() => {
    const handler = (e) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // Phase 2.2.1 — One-time toast in Studio when the client requested
  // changes. Session-scoped (sessionStorage), zero persistence, no new
  // schema. Fetches the existing review-link meta endpoint.
  useEffect(() => {
    if (isTemplateMode) return; // template editor has no review link
    if (!eventId || !token) return;
    const seenKey = `review-note-shown-${eventId}`;
    if (sessionStorage.getItem(seenKey)) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/events/${eventId}/review-link`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const note = (r.data?.review_note || '').trim();
        if (r.data?.review_status === 'changes_requested' && note) {
          toast.info(`Review note from your client: ${note}`, { duration: 5000 });
          sessionStorage.setItem(seenKey, '1');
        }
      } catch {
        /* silent — review link not yet enabled, or fetch failed */
      }
    })();
    return () => { cancelled = true; };
  }, [eventId, token, isTemplateMode]);

  // ── Keyboard shortcuts: Ctrl+Z / Ctrl+Shift+Z ──
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          history.redo();
        } else {
          history.undo();
        }
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
        e.preventDefault();
        history.redo();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [history]);

  // Load invitation data (event mode) — or template content (template mode)
  useEffect(() => {
    const load = async () => {
      try {
        // Phase 2.0.1 — template mode reads from the in-memory template
        // payload instead of an event's rsvp-page. Shape the same `data`
        // dict so the downstream merge logic stays identical.
        let data;
        if (isTemplateMode) {
          if (!initialTemplate) {
            toast.error('Template not loaded');
            navigate('/studio/templates');
            return;
          }
          const snap = initialTemplate.snapshot || {};
          data = {
            v2_template_id: snap.v2_template_id
              || initialTemplate.base_template_id
              || 'freeform-studio-v1',
            v2_content: snap.v2_content || {},
            v2_theme_overrides: snap.v2_theme_overrides || {},
            v2_settings_overrides: snap.v2_settings_overrides || {},
            v2_translations: {}, // templates don't store translations (yet)
            layout_version: initialTemplate.layout_version
              || snap.layout_version
              || 'legacy-flat',
            layout_mode: initialTemplate.layout_mode
              || snap.layout_mode
              || 'free',
            slug: '',           // templates have no public invite slug
            is_published: false,
            updated_at: initialTemplate.updated_at,
            preview_token: null,
          };
        } else {
          const res = await axios.get(`${API}/api/events/${eventId}/rsvp-page`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          data = res.data;
        }
        const tmplId = data.v2_template_id || 'elegant-wedding-v1';
        const tmpl = TEMPLATES[tmplId];
        if (!tmpl) { toast.error('Template not found'); return; }

        setTemplateConfig(tmpl);
        setContent(data.v2_content || {});
        setThemeOverrides(data.v2_theme_overrides || {});
        setSlug(data.slug || '');
        setIsPublished(data.is_published || false);
        setV2Translations(data.v2_translations || {});
        // Phase 1.7 — meta for toolbar QA helpers (Preview on phone, health check)
        setRsvpPageInfo({
          slug: data.slug || '',
          layout_version: data.layout_version || 'legacy-flat',
          layout_mode: data.layout_mode || 'free',
          updated_at: data.updated_at,
          is_published: data.is_published || false,
          preview_token: data.preview_token,
        });

        // Fetch event languages (event mode only)
        if (!isTemplateMode) {
          try {
            const evRes = await axios.get(`${API}/api/events/${eventId}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            setEventLanguages(evRes.data?.languages || []);
            setEventDefaultLanguage(evRes.data?.default_language || 'en');
          } catch { /* */ }
        }

        // Merge template default sections with any saved dynamic sections/overrides
        const savedDynamic = data.v2_settings_overrides?.dynamicSections;
        const savedOverrides = data.v2_settings_overrides?.sectionVisibility || {};
        const savedVariants = data.v2_settings_overrides?.sectionVariants || {};
        const savedOrder = data.v2_settings_overrides?.sectionOrder || [];
        // Motion 1.0 — hydrate motion preset from saved settings.
        setMotionPreset(data.v2_settings_overrides?.motionPreset || 'none');

        let mergedSections;
        if (savedDynamic && savedDynamic.length > 0) {
          // Use saved dynamic sections (includes user-added sections, backgrounds, heights)
          // Merge elements from template defaults for template sections
          const tmplById = {};
          (tmpl.sections || []).forEach(s => { tmplById[s.id] = s; });
          mergedSections = savedDynamic.map(saved => ({
            ...(tmplById[saved.id] || {}),
            ...saved,
            // Keep template elements as base, content overrides applied at render time
            elements: tmplById[saved.id]?.elements || saved.elements || [],
          }));
        } else {
          // Legacy: use template sections with visibility/variant overrides
          mergedSections = tmpl.sections.map(s => ({
            ...s,
            visible: savedOverrides[s.id] !== undefined ? savedOverrides[s.id] : s.visible,
            variant: savedVariants[s.id] || s.variant,
          }));
          // Re-order sections if saved order exists
          if (savedOrder.length > 0) {
            const byId = {};
            mergedSections.forEach(s => { byId[s.id] = s; });
            const ordered = savedOrder.map(id => byId[id]).filter(Boolean);
            mergedSections.forEach(s => { if (!savedOrder.includes(s.id)) ordered.push(s); });
            mergedSections = ordered;
          }
        }
        // In Studio mode, ensure non-collage sections have canvasYPosition
        if (tmpl.isStudio) {
          let idx = 0;
          mergedSections = mergedSections.map(s => {
            if (s.type !== 'collage' && s.canvasYPosition == null) {
              idx++;
              return { ...s, canvasYPosition: Math.min(0.3 + idx * 0.2, 0.9) };
            }
            return s;
          });
        }
        setSections(mergedSections);
      } catch (err) {
        toast.error(isTemplateMode ? 'Failed to load template' : 'Failed to load invitation');
        navigate(isTemplateMode ? '/studio/templates' : `/events/${eventId}`);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [eventId, token, navigate, isTemplateMode, initialTemplate, setContent]);

  // Phase 1.9 — refetch rsvp-page after a snapshot restore, then rehydrate
  // the local `content` so the canvas reflects the restored state.
  const refetchInvitation = useCallback(async () => {
    if (!eventId || !token) return;
    try {
      const res = await axios.get(`${API}/api/events/${eventId}/rsvp-page`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.data || {};
      setContent(data.v2_content || {});
      setThemeOverrides(data.v2_theme_overrides || {});
      setRsvpPageInfo((prev) => ({
        ...(prev || {}),
        slug: data.slug || prev?.slug || '',
        layout_version: data.layout_version || prev?.layout_version || 'legacy-flat',
        layout_mode: data.layout_mode || prev?.layout_mode || 'free',
        updated_at: data.updated_at,
        is_published: data.is_published ?? prev?.is_published ?? false,
      }));
      setSelectedElement(null);
      setSelectionSet([]);
    } catch (err) {
      toast.error('Could not refresh invitation after restore');
    }
  }, [eventId, token, setContent]);

  // Phase 1.9 — load opener config once, save via dedicated PATCH endpoint.
  useEffect(() => {
    if (!eventId || !token) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/events/${eventId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setOpenerConfig(res.data?.opener || null);
      } catch { /* ignore */ }
    })();
    // Phase 1.9.19 — load entry cover. Same /events/{id} endpoint already
    // surfaces the field; fetch once on mount.
    (async () => {
      if (!eventId || !token) return;
      try {
        const res = await axios.get(`${API}/api/events/${eventId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setEntryCoverConfig(res.data?.entry_cover || null);
      } catch { /* ignore */ }
    })();
  }, [eventId, token]);

  const handleSaveOpener = useCallback(async (next) => {
    if (!eventId || !token) return;
    try {
      const body = next == null ? { presetId: null } : next;
      const res = await axios.patch(`${API}/api/events/${eventId}/opener`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOpenerConfig(res.data?.opener || null);
    } catch (err) {
      toast.error('Could not save opener');
    }
  }, [eventId, token]);

  // Phase 1.9.19 — Entry Cover save. Mirrors opener saver. Persists the
  // whole flat draft object to PATCH /entry-cover; backend strips unknown
  // keys and merges with existing record.
  const handleSaveEntryCover = useCallback(async (next) => {
    if (!eventId || !token) return null;
    try {
      const res = await axios.patch(`${API}/api/events/${eventId}/entry-cover`, next || {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const saved = res.data?.entry_cover || null;
      setEntryCoverConfig(saved);
      return saved;
    } catch (err) {
      toast.error('Could not save Entry Cover');
      return null;
    }
  }, [eventId, token]);

  const handleDataChange = useCallback((sectionId, newData) => {
    setContent(prev => ({ ...prev, [sectionId]: newData }));
  }, []);

  const handleToggle = useCallback((sectionId) => {
    setSections(prev => prev.map(s => s.id === sectionId ? { ...s, visible: !s.visible } : s));
  }, []);

  const handleVariantChange = useCallback((sectionId, variantId) => {
    setSections(prev => prev.map(s => s.id === sectionId ? { ...s, variant: variantId } : s));
  }, []);

  const handleMoveSection = useCallback((fromIdx, toIdx) => {
    setSections(prev => {
      const updated = [...prev];
      const [moved] = updated.splice(fromIdx, 1);
      updated.splice(toIdx, 0, moved);
      return updated;
    });
  }, []);

  // ─── Section Manager Handlers ────────────────────────────
  const [uploadingSectionBg, setUploadingSectionBg] = useState(null);

  const handleAddSection = useCallback((type) => {
    // In Studio mode, slideshow becomes a canvas element instead of inline widget
    if (templateConfig?.isStudio && type === 'slideshow') {
      const collageSec = sections.find(s => s.type === 'collage');
      if (collageSec) {
        const sectionId = collageSec.id;
        const currentElements = content[sectionId]?._elements ||
          templateConfig?.sections?.find(s => s.id === sectionId)?.elements || [];
        const newId = `custom-slideshow_element-${Date.now()}`;
        const newEl = {
          type: 'slideshow_element', images: [], transition: 'slide', speed: 'medium',
          aspectRatio: '16/9', rotation: 0, id: newId,
          layout: { desktop: { x: 0.5, y: 0.3, w: 0.5, z: 8 }, mobile: { x: 0.5, y: 0.3, w: 0.85, z: 8 } },
        };
        setContent(prev => ({
          ...prev,
          [sectionId]: { ...(prev[sectionId] || {}), _elements: [...currentElements, newEl] },
        }));
        setSelectedElement({ sectionId, elementId: newId });
        toast.success('Added Slideshow element');
        return;
      }
    }

    const factory = SECTION_DEFAULTS[type];
    if (!factory) { toast.error(`Unknown section type: ${type}`); return; }
    const newSection = { ...factory(), id: `${type}-${Date.now()}` };
    // In Studio mode, widgets render inline at a Y position within the canvas
    // Hero sections are standalone full-screen sections, not inline widgets
    if (templateConfig?.isStudio && type !== 'hero') {
      newSection.canvasYPosition = 0.5;
    }
    // Hero sections should be placed at the top
    if (type === 'hero') {
      setSections(prev => [newSection, ...prev]);
    } else {
      setSections(prev => [...prev, newSection]);
    }
    toast.success(`Added ${newSection.label} section`);
  }, [templateConfig, sections, content, setContent, setSelectedElement]);

  const handleRemoveSection = useCallback((sectionId) => {
    setSections(prev => prev.filter(s => s.id !== sectionId));
    // Clean up content for this section
    setContent(prev => {
      const next = { ...prev };
      delete next[sectionId];
      return next;
    });
  }, [setContent]);

  const handleMoveSectionById = useCallback((sectionId, direction) => {
    setSections(prev => {
      const idx = prev.findIndex(s => s.id === sectionId);
      if (idx < 0) return prev;
      const target = direction === 'up' ? idx - 1 : idx + 1;
      if (target < 0 || target >= prev.length) return prev;
      const updated = [...prev];
      [updated[idx], updated[target]] = [updated[target], updated[idx]];
      return updated;
    });
  }, []);

  const handleUpdateSection = useCallback((sectionId, updates) => {
    setSections(prev => prev.map(s => s.id === sectionId ? { ...s, ...updates } : s));
  }, []);

  const handleUpdateCanvasYPosition = useCallback((sectionId, yPosition) => {
    setSections(prev => prev.map(s =>
      s.id === sectionId ? { ...s, canvasYPosition: yPosition } : s
    ));
  }, []);

  const handleUpdateSectionData = useCallback((sectionId, newData) => {
    setSections(prev => prev.map(s =>
      s.id === sectionId ? { ...s, data: newData } : s
    ));
  }, []);

  const handleSectionBgUpload = useCallback(async (sectionId, file) => {
    setUploadingSectionBg(sectionId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(
        `${API}/api/events/${eventId}/rsvp-page/upload-image`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      const url = res.data.image_url || res.data.url;
      setSections(prev => prev.map(s =>
        s.id === sectionId ? { ...s, background: { type: 'image', src: url } } : s
      ));
      toast.success('Background uploaded');
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploadingSectionBg(null);
    }
  }, [eventId, token]);

  // ── Global background upload (paper texture, etc.) ──
  const handleGlobalBgUpload = useCallback(async (file) => {
    setUploadingGlobalBg(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      // Global page-wide backgrounds use the tall profile (1440x8000 max,
      // 25 MB ceiling) so long editorial scroll experiences aren't crushed
      // down to the default 1600x2400 envelope.
      const res = await axios.post(
        `${API}/api/events/${eventId}/rsvp-page/upload-image?profile=full_page_background`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      const url = res.data.image_url || res.data.url;
      setThemeOverrides(prev => ({ ...prev, globalBg: url }));
      // Capture optimizer metadata for the subtle "Optimized:" info pill.
      // Pre-upload source `file.size` is the only honest size we have client-side
      // (server doesn't return compressed bytes today and we don't want to add
      // a roundtrip just for a pill). Optimized output is typically smaller, so
      // we surface the original payload size for designer reference.
      if (res.data?.width && res.data?.height) {
        setGlobalBgMeta({
          width: res.data.width,
          height: res.data.height,
          profile: res.data.profile || 'default',
          sizeBytes: file?.size || null,
        });
      } else {
        setGlobalBgMeta(null);
      }
      toast.success('Background image uploaded');
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploadingGlobalBg(false);
    }
  }, [eventId, token]);

  const handleGlobalBgRemove = useCallback(() => {
    setGlobalBgMeta(null);
    setThemeOverrides(prev => {
      const next = { ...prev };
      delete next.globalBg;
      return next;
    });
  }, []);

  // ── Studio: background solid color change ──
  const handleStudioBgColor = useCallback((color) => {
    setSections(prev => prev.map(s =>
      s.type === 'collage' ? { ...s, background: { type: 'color', color } } : s
    ));
    // Also remove global bg image when switching to solid color
    setThemeOverrides(prev => {
      const next = { ...prev };
      delete next.globalBg;
      return next;
    });
  }, []);

  const handleElementStyleUpdate = useCallback((sectionId, elementId, styleUpdates) => {
    setContent(prev => ({
      ...prev,
      [sectionId]: {
        ...(prev[sectionId] || {}),
        [elementId]: {
          ...(prev[sectionId]?.[elementId] || {}),
          ...styleUpdates,
        },
      },
    }));
  }, [setContent]);

  const handleImageUpload = useCallback(async (sectionId, field, file) => {
    const key = `${sectionId}.${field}`;
    setUploadingField(key);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(
        `${API}/api/events/${eventId}/rsvp-page/upload-image`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      const url = res.data.image_url || res.data.url;

      // Check if this is a widget section (slideshow, gallery, etc.)
      const isWidgetSection = sections.some(s => s.id === sectionId && ['slideshow', 'gallery'].includes(s.type));

      if (isWidgetSection && field === 'images') {
        // Update section.data.images directly
        setSections(prev => prev.map(s => {
          if (s.id !== sectionId) return s;
          const existing = s.data?.images || [];
          return { ...s, data: { ...s.data, images: [...existing, url] } };
        }));
      } else if (field === 'images') {
        // Legacy: append to content state for hero/section panels
        setContent(prev => {
          const existing = prev[sectionId]?.images || [];
          return { ...prev, [sectionId]: { ...prev[sectionId], images: [...existing, url] } };
        });
      } else {
        setContent(prev => ({ ...prev, [sectionId]: { ...prev[sectionId], [field]: url } }));
      }
      toast.success('Image uploaded');
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploadingField(null);
    }
  }, [eventId, token, sections]);

  // ─── Collage-specific handlers ────────────────────────────
  const handleCollageElementUpdate = useCallback((sectionId, elementId, overrides) => {
    setContent(prev => ({
      ...prev,
      [sectionId]: {
        ...(prev[sectionId] || {}),
        [elementId]: overrides,
      },
    }));
  }, []);

  const handleCollageImageUpload = useCallback(async (sectionId, elementId, field, file) => {
    const key = `${sectionId}.${elementId}.${field}`;
    setUploadingField(key);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(
        `${API}/api/events/${eventId}/rsvp-page/upload-image`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      const url = res.data.image_url || res.data.url;
      setContent(prev => ({
        ...prev,
        [sectionId]: {
          ...(prev[sectionId] || {}),
          [elementId]: {
            ...(prev[sectionId]?.[elementId] || {}),
            [field]: url,
          },
        },
      }));
      toast.success('Image uploaded');
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploadingField(null);
    }
  }, [eventId, token]);

  // ─── Collage structural mutations ─────────────────────────
  const getElementList = useCallback((sectionId) => {
    const custom = content[sectionId]?._elements;
    if (custom) return custom;
    // Fall back to template defaults
    const tplSection = templateConfig?.sections?.find(s => s.id === sectionId);
    return tplSection?.elements || [];
  }, [content, templateConfig]);

  const handleCollageDuplicate = useCallback((sectionId, element) => {
    const currentElements = getElementList(sectionId);
    const newId = `custom-${element.type}-${Date.now()}`;
    const dup = JSON.parse(JSON.stringify(element));
    dup.id = newId;
    // Offset position slightly
    if (dup.layout?.desktop) dup.layout.desktop.x = Math.min(0.92, dup.layout.desktop.x + 0.05);
    if (dup.layout?.mobile) dup.layout.mobile.x = Math.min(0.92, dup.layout.mobile.x + 0.05);
    if (dup.layout?.desktop) dup.layout.desktop.y = Math.min(0.92, dup.layout.desktop.y + 0.04);
    if (dup.layout?.mobile) dup.layout.mobile.y = Math.min(0.92, dup.layout.mobile.y + 0.04);
    setContent(prev => ({
      ...prev,
      [sectionId]: { ...(prev[sectionId] || {}), _elements: [...currentElements, dup] },
    }));
    setSelectedElement({ sectionId, elementId: newId });
    toast.success('Element duplicated');
  }, [getElementList]);

  const handleCollageRemove = useCallback((sectionId, elementId) => {
    const currentElements = getElementList(sectionId);
    const filtered = currentElements.filter(el => el.id !== elementId);
    setContent(prev => {
      const sectionData = { ...(prev[sectionId] || {}) };
      sectionData._elements = filtered;
      delete sectionData[elementId]; // Remove per-element overrides too
      return { ...prev, [sectionId]: sectionData };
    });
    if (selectedElement?.elementId === elementId) setSelectedElement(null);
    toast.success('Element removed');
  }, [getElementList, selectedElement]);

  const handleCollageAdd = useCallback((sectionId, presetOrType) => {
    const currentElements = getElementList(sectionId);
    // Always place new elements on top of everything that exists (last added = top)
    const sectionContent = content[sectionId] || {};
    const maxZ = currentElements.reduce((m, e) => {
      const ov = sectionContent[e.id] || {};
      const layout = ov.layout || e.layout || {};
      const z = (layout.mobile?.z ?? layout.desktop?.z ?? 1);
      return Math.max(m, z);
    }, 0);
    const nextZ = Math.min(20, maxZ + 1);
    let newEl;

    if (typeof presetOrType === 'string') {
      // Legacy: type string (from old AddElementMenu)
      const defaults = {
        image_card: { type: 'image_card', style: 'plain-photo', src: '', caption: '', rotation: 0, intent: { role: 'image', importance: 'medium', behavior: 'flexible', mobileBehavior: 'preserve', overlapPolicy: 'avoidText', animationPriority: 3 }, layout: { desktop: { x: 0.5, y: 0.15, w: 0.3, z: nextZ }, mobile: { x: 0.5, y: 0.15, w: 0.6, z: nextZ } } },
        text_note: { type: 'text_note', style: 'handwritten-paper', content: 'Your note here...', rotation: 0, intent: { role: 'decorative', importance: 'medium', behavior: 'flexible', mobileBehavior: 'preserve', overlapPolicy: 'allow', animationPriority: 4 }, layout: { desktop: { x: 0.5, y: 0.15, w: 0.22, z: nextZ }, mobile: { x: 0.5, y: 0.15, w: 0.42, z: nextZ } } },
        text_block: { type: 'text_block', style: 'typewriter', content: 'Your text here...', intent: { role: 'bodyText', importance: 'medium', behavior: 'flexible', mobileBehavior: 'preserve', overlapPolicy: 'avoidImportant', animationPriority: 3 }, layout: { desktop: { x: 0.5, y: 0.15, w: 0.28, z: nextZ }, mobile: { x: 0.5, y: 0.15, w: 0.62, z: nextZ } } },
        title_block: { type: 'title_block', style: 'hero-title', title: 'Your Title', subtitle: '', meta: '', buttonText: '', buttonHref: '', intent: { role: 'primaryHeadline', importance: 'high', behavior: 'fixed', mobileBehavior: 'preserve', overlapPolicy: 'none', animationPriority: 1 }, layout: { desktop: { x: 0.5, y: 0.15, w: 0.6, z: nextZ }, mobile: { x: 0.5, y: 0.15, w: 0.84, z: nextZ } } },
        vinyl_player: { type: 'vinyl_player', playerStyle: 'vinyl', musicUrl: '', songTitle: 'Our Song', artistName: 'Wedding Soundtrack', coverImage: '', accentColor: '', bgColor: '', layout: { desktop: { x: 0.85, y: 0.05, w: 0.22, z: nextZ }, mobile: { x: 0.82, y: 0.06, w: 0.28, z: nextZ } } },
        slideshow_element: { type: 'slideshow_element', images: [], transition: 'slide', speed: 'medium', aspectRatio: '16/9', rotation: 0, layout: { desktop: { x: 0.5, y: 0.3, w: 0.5, z: nextZ }, mobile: { x: 0.5, y: 0.3, w: 0.85, z: nextZ } } },
        rsvp_button: { type: 'rsvp_button', content: 'RSVP', customStyle: { borderRadius: 999, borderWidth: 1, paddingX: 28, paddingY: 14, letterSpacing: 1.6, textTransform: 'uppercase', fontWeight: 500 }, intent: { role: 'action', importance: 'high', behavior: 'fixed', mobileBehavior: 'preserve', overlapPolicy: 'none', animationPriority: 2 }, layout: { desktop: { x: 0.5, y: 0.45, w: 0.18, z: nextZ }, mobile: { x: 0.5, y: 0.6, w: 0.56, z: nextZ } } },
      };
      const newId = `custom-${presetOrType}-${Date.now()}`;
      newEl = { ...defaults[presetOrType], id: newId };
    } else {
      // Preset object (from PresetPicker) — overwrite z so it lands on top
      const preset = JSON.parse(JSON.stringify(presetOrType));
      const newId = `custom-${preset.type}-${Date.now()}`;
      delete preset.name;
      delete preset.description;
      delete preset.category;
      if (preset.layout) {
        if (preset.layout.mobile) preset.layout.mobile.z = nextZ;
        if (preset.layout.desktop) preset.layout.desktop.z = nextZ;
      }
      newEl = { ...preset, id: newId };
    }

    setContent(prev => ({
      ...prev,
      [sectionId]: { ...(prev[sectionId] || {}), _elements: [...currentElements, newEl] },
    }));
    setSelectedElement({ sectionId, elementId: newEl.id });
    toast.success('Element added');
  }, [getElementList, content]);

  // Phase 1.9.1 — "Photo row" prefab. Inserts 3 image_card placeholders
  // pre-arranged using the same row math as the multi-select "Convert to
  // photo row" action. Each photo stays individually editable afterwards —
  // no smart group / parentId is created.
  const handleAddPhotoRow = useCallback((sectionId) => {
    const currentElements = getElementList(sectionId);
    const sectionContent = content[sectionId] || {};
    const maxZ = currentElements.reduce((m, e) => {
      const ov = sectionContent[e.id] || {};
      const layout = ov.layout || e.layout || {};
      const z = (layout.mobile?.z ?? layout.desktop?.z ?? 1);
      return Math.max(m, z);
    }, 0);
    const nextZ = Math.min(20, maxZ + 1);
    const ts = Date.now();

    // Build 3 placeholder image_card elements at a sensible default y (35%).
    const stubs = [0, 1, 2].map((i) => ({
      id: `photo-row-${ts}-${i}`,
      type: 'image_card',
      style: 'plain-photo',
      role: IMAGE_ROLES.PHOTO,
      src: '',
      caption: '',
      rotation: 0,
      intent: { role: 'image', importance: 'medium', behavior: 'flexible',
                mobileBehavior: 'preserve', overlapPolicy: 'avoidText',
                animationPriority: 3 },
      customStyle: { fit: 'cover', position: 'center', aspectRatio: '1:1' },
      // Seed identical y so computePhotoRowOverrides averages to the same value.
      layout: { mobile:  { x: 0.5, y: 0.35, w: 0.3, z: nextZ },
                desktop: { x: 0.5, y: 0.35, w: 0.3, z: nextZ } },
    }));

    const rowOverrides = computePhotoRowOverrides(stubs);

    // Merge each stub's base + row override so the inserted element already
    // sits in its final row slot. We bake the overrides directly into
    // `_elements` (rather than into per-id overrides) so newly-added photos
    // travel cleanly through the save adapter.
    const newEls = stubs.map((stub, i) => {
      const ov = rowOverrides[i] || {};
      return {
        ...stub,
        role: ov.role || stub.role,
        customStyle: { ...(stub.customStyle || {}), ...(ov.customStyle || {}) },
        layout: {
          ...(stub.layout || {}),
          mobile:  { ...(stub.layout?.mobile  || {}), ...(ov.layout?.mobile  || {}) },
          desktop: { ...(stub.layout?.desktop || {}), ...(ov.layout?.desktop || {}) },
        },
      };
    });

    setContent((prev) => ({
      ...prev,
      [sectionId]: {
        ...(prev[sectionId] || {}),
        _elements: [...currentElements, ...newEls],
      },
    }));

    // Select the middle one and clear any prior multi-select.
    setSelectedElement({ sectionId, elementId: newEls[1].id });
    setSelectionSet([]);
    toast.success('Photo row added');
  }, [getElementList, content, setContent]);

  const handleAddShape = useCallback((sectionId, shapeStyle) => {
    const currentElements = getElementList(sectionId);
    const sectionContent = content[sectionId] || {};
    // Place new shape on top of everything that exists (matches handleCollageAdd).
    const maxZ = currentElements.reduce((m, e) => {
      const ov = sectionContent[e.id] || {};
      const layout = ov.layout || e.layout || {};
      const z = (layout.mobile?.z ?? layout.desktop?.z ?? 1);
      return Math.max(m, z);
    }, 0);
    const nextZ = Math.min(20, maxZ + 1);
    // Shape-specific defaults matched to ShapeElement.js renderer contract:
    // type='shape', style='horizontal_line'|'rectangle'|'circle', plus
    // customStyle.{fill,stroke,strokeWidth,opacity,borderRadius,aspectRatio}.
    const shapeDefaults = {
      horizontal_line: {
        type: 'shape', style: 'horizontal_line',
        customStyle: { stroke: '#1a1a1a', strokeWidth: 2, opacity: 1, borderRadius: 999 },
        layout: {
          desktop: { x: 0.5, y: 0.5, w: 0.3, z: nextZ },
          mobile:  { x: 0.5, y: 0.5, w: 0.6, z: nextZ },
        },
      },
      rectangle: {
        type: 'shape', style: 'rectangle',
        customStyle: { fill: '#e8ddd5', stroke: '#1a1a1a', strokeWidth: 0, opacity: 1, borderRadius: 0, aspectRatio: '3/2' },
        layout: {
          desktop: { x: 0.5, y: 0.5, w: 0.25, z: nextZ },
          mobile:  { x: 0.5, y: 0.5, w: 0.5,  z: nextZ },
        },
      },
      circle: {
        type: 'shape', style: 'circle',
        customStyle: { fill: '#e8ddd5', stroke: '#1a1a1a', strokeWidth: 0, opacity: 1 },
        layout: {
          desktop: { x: 0.5, y: 0.5, w: 0.2, z: nextZ },
          mobile:  { x: 0.5, y: 0.5, w: 0.4, z: nextZ },
        },
      },
    };
    const preset = shapeDefaults[shapeStyle];
    if (!preset) {
      toast.error('Unknown shape');
      return;
    }
    const newId = `shape-${shapeStyle}-${Date.now()}`;
    const newEl = { ...JSON.parse(JSON.stringify(preset)), id: newId };
    setContent(prev => ({
      ...prev,
      [sectionId]: { ...(prev[sectionId] || {}), _elements: [...currentElements, newEl] },
    }));
    setSelectedElement({ sectionId, elementId: newId });
    toast.success('Shape added');
  }, [getElementList, content]);

  const handleAddOrnament = useCallback((sectionId, ornamentId) => {
    const currentElements = getElementList(sectionId);
    const sectionContent = content[sectionId] || {};
    const maxZ = currentElements.reduce((m, e) => {
      const ov = sectionContent[e.id] || {};
      const layout = ov.layout || e.layout || {};
      const z = (layout.mobile?.z ?? layout.desktop?.z ?? 1);
      return Math.max(m, z);
    }, 0);
    const nextZ = Math.min(20, maxZ + 1);
    const newId = `ornament-${ornamentId}-${Date.now()}`;
    const newEl = {
      type: 'ornament',
      style: ornamentId,
      color: '#1a1a1a',
      layout: { desktop: { x: 0.5, y: 0.12, w: 0.55, z: nextZ }, mobile: { x: 0.5, y: 0.12, w: 0.55, z: nextZ } },
      id: newId,
    };
    setContent(prev => ({
      ...prev,
      [sectionId]: { ...(prev[sectionId] || {}), _elements: [...currentElements, newEl] },
    }));
    setSelectedElement({ sectionId, elementId: newId });
    toast.success('Ornament added');
  }, [getElementList, content]);

  const handleUploadOrnament = useCallback(async (sectionId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/api/events/${eventId}/rsvp-page/upload-image`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      const imageUrl = res.data.image_url || res.data.url;
      const currentElements = getElementList(sectionId);
      const newId = `ornament-custom-${Date.now()}`;
      const newEl = {
        type: 'ornament',
        style: 'custom',
        src: imageUrl,
        name: file.name,
        layout: { desktop: { x: 0.5, y: 0.15, w: 0.3, z: 4 }, mobile: { x: 0.5, y: 0.15, w: 0.3, z: 4 } },
        id: newId,
      };
      setContent(prev => ({
        ...prev,
        [sectionId]: { ...(prev[sectionId] || {}), _elements: [...currentElements, newEl] },
      }));
      setSelectedElement({ sectionId, elementId: newId });
      toast.success('Custom ornament uploaded');
    } catch {
      toast.error('Upload failed');
    }
  }, [getElementList, eventId, token]);

  // Upload image for a canvas image element (click-to-upload on ImageCard placeholder)
  const handleCanvasImageUpload = useCallback(async (elementId, file) => {
    try {
      // Pre-read the file to capture its natural aspect ratio so the element
      // resizes to match the photo's intrinsic shape (square stays square, etc.)
      let naturalAspect = null;
      try {
        const dims = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onerror = reject;
          reader.onload = (e) => {
            const img = new window.Image();
            img.onerror = reject;
            img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
            img.src = e.target.result;
          };
          reader.readAsDataURL(file);
        });
        if (dims.w && dims.h) {
          naturalAspect = (dims.w / dims.h).toFixed(4);
        }
      } catch (_) { /* aspect detection is best-effort */ }

      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/api/events/${eventId}/rsvp-page/upload-image`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      const imageUrl = res.data.image_url || res.data.url;
      // Find which section this element belongs to and update its src + naturalAspect
      setContent(prev => {
        const next = { ...prev };
        for (const sectionId of Object.keys(next)) {
          const elements = next[sectionId]?._elements;
          if (Array.isArray(elements)) {
            const idx = elements.findIndex(e => e.id === elementId);
            if (idx >= 0) {
              const updated = [...elements];
              updated[idx] = {
                ...updated[idx],
                src: imageUrl,
                ...(naturalAspect ? { naturalAspect } : {}),
              };
              next[sectionId] = { ...next[sectionId], _elements: updated };
              break;
            }
          }
        }
        return next;
      });
      toast.success('Image uploaded');
    } catch {
      toast.error('Upload failed');
    }
  }, [eventId, token, setContent]);


  const handleSavePreset = useCallback(async (element, overrides) => {
    const merged = { ...element, ...overrides };
    // Strip runtime-only fields
    delete merged.id;
    const name = prompt('Preset name:', element.id?.replace(/-/g, ' ') || 'My Preset');
    if (!name) return;
    try {
      await axios.post(`${API}/api/user/element-presets`, {
        name,
        description: '',
        element: merged,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Saved to My Presets');
    } catch {
      toast.error('Failed to save preset');
    }
  }, [token]);

  const handleCollageElementMove = useCallback((sectionId, elementId, x, y) => {
    // Update the layout position for the current layout mode (always mobile in phone preview).
    // Keep BOTH legacy (x,y) and section-v1 percent fields in sync. The
    // renderer reads `xPercent`/`yPercent` first (sectionLayout.js:47-49)
    // and only falls back to legacy on missing — so stale percent values
    // would silently override fresh legacy coords until the backend
    // save adapter re-normalizes. That caused "moved image still shows
    // at old position" + "full-width image still has padding" bugs.
    setContent(prev => {
      const sectionData = { ...(prev[sectionId] || {}) };
      const existing = sectionData[elementId] || {};
      const existingLayout = existing.layout || {};
      const mobileLayout = existingLayout.mobile || {};
      const desktopLayout = existingLayout.desktop || {};
      sectionData[elementId] = {
        ...existing,
        layout: {
          ...existingLayout,
          mobile: { ...mobileLayout, x, y, xPercent: x * 100, yPercent: y * 100 },
          desktop: { ...desktopLayout, x, y, xPercent: x * 100, yPercent: y * 100 },
        },
      };
      return { ...prev, [sectionId]: sectionData };
    });
  }, []);

  const handleCollageElementResize = useCallback((sectionId, elementId, newWidth) => {
    setContent(prev => {
      const sectionData = { ...(prev[sectionId] || {}) };
      const existing = sectionData[elementId] || {};
      const existingLayout = existing.layout || {};
      const mobileLayout = existingLayout.mobile || {};
      const desktopLayout = existingLayout.desktop || {};
      // Same percent-sync as move (see comment above). Without
      // `widthPercent` update, the renderer would keep using the
      // stale percent and a "Full" click would still render at the
      // previous (e.g. 85%) width.
      const mobileUpdate = { ...mobileLayout, w: newWidth, widthPercent: newWidth * 100 };
      const desktopUpdate = { ...desktopLayout, w: newWidth, widthPercent: newWidth * 100 };
      // Auto-center when going full-width for edge-to-edge coverage
      if (newWidth >= 0.95) {
        mobileUpdate.x = 0.5;
        mobileUpdate.xPercent = 50;
        desktopUpdate.x = 0.5;
        desktopUpdate.xPercent = 50;
      }
      sectionData[elementId] = {
        ...existing,
        layout: {
          ...existingLayout,
          mobile: mobileUpdate,
          desktop: desktopUpdate,
        },
      };
      return { ...prev, [sectionId]: sectionData };
    });
  }, []);

  const handleCollageElementRotate = useCallback((sectionId, elementId, newRotation) => {
    setContent(prev => {
      const sectionData = { ...(prev[sectionId] || {}) };
      const existing = sectionData[elementId] || {};
      sectionData[elementId] = {
        ...existing,
        rotation: newRotation,
      };
      return { ...prev, [sectionId]: sectionData };
    });
  }, []);

  const handleDeleteElement = useCallback((sectionId, elementId) => {
    setContent(prev => {
      const sectionData = { ...(prev[sectionId] || {}) };
      const elements = (sectionData._elements || []).filter(e => e.id !== elementId);
      sectionData._elements = elements;
      // Also remove element overrides
      delete sectionData[elementId];
      return { ...prev, [sectionId]: sectionData };
    });
    setSelectedElement(null);
    setEditingElement(null);
    toast.success('Element deleted');
  }, [setContent]);


  const handleAutoBalance = useCallback((device) => {
    // For each collage section, merge base elements with overrides, run auto-balance, write back
    setContent(prev => {
      const next = { ...prev };
      for (const section of sections) {
        if (section.type !== 'collage') continue;
        const baseElements = section.elements || [];
        // Merge overrides into elements
        const merged = baseElements.map(el => {
          const overrides = prev[section.id]?.[el.id] || {};
          const mergedLayout = overrides.layout ? {
            ...el.layout,
            ...overrides.layout,
            desktop: { ...(el.layout?.desktop || {}), ...(overrides.layout?.desktop || {}) },
            mobile: { ...(el.layout?.mobile || {}), ...(overrides.layout?.mobile || {}) },
          } : el.layout;
          return { ...el, ...overrides, layout: mergedLayout };
        });

        const balanced = autoBalanceLayout(merged, {
          device,
          targetCenter: device === 'mobile' ? 0.46 : 0.5,
          deadZone: device === 'mobile' ? 0.02 : 0.015,
          maxShift: device === 'mobile' ? 0.04 : 0.05,
          strength: device === 'mobile' ? 0.6 : 0.65,
        });

        // Write position deltas back as content overrides
        const sectionData = { ...(next[section.id] || {}) };
        for (const balEl of balanced) {
          const baseEl = baseElements.find(e => e.id === balEl.id);
          if (!baseEl) continue;
          const existing = sectionData[balEl.id] || {};
          const existingLayout = existing.layout || {};
          sectionData[balEl.id] = {
            ...existing,
            layout: {
              ...existingLayout,
              desktop: { ...(existingLayout.desktop || {}), x: balEl.layout.desktop.x },
              mobile: { ...(existingLayout.mobile || balEl.layout.desktop), x: (balEl.layout.mobile || balEl.layout.desktop).x },
            },
          };
        }
        next[section.id] = sectionData;
      }
      return next;
    });
    toast.success(`Auto-balanced (${device})`);
  }, [sections, setContent]);

  const handleStylePresetSelect = useCallback((preset) => {
    setActiveStylePreset(preset.id);
    setThemeOverrides(prev => ({
      ...prev,
      palette: preset.palette,
      fontPair: preset.fontPair,
      themeExtensions: {
        ...(templateConfig?.theme?.themeExtensions || {}),
        ...(preset.themeExtensions || {}),
      },
    }));
  }, [templateConfig]);

  const handleLayerZUpdate = useCallback((sectionId, elementId, field, value) => {
    setContent(prev => {
      const sectionData = { ...(prev[sectionId] || {}) };
      const existing = sectionData[elementId] || {};
      const existingLayout = existing.layout || {};
      const mobileLayout = existingLayout.mobile || {};
      const desktopLayout = existingLayout.desktop || {};
      sectionData[elementId] = {
        ...existing,
        layout: {
          ...existingLayout,
          mobile: { ...mobileLayout, [field]: value },
          desktop: { ...desktopLayout, [field]: value },
        },
      };
      return { ...prev, [sectionId]: sectionData };
    });
  }, [setContent]);

  // Phase 1.8 — toggle element lock (editor-only, no public effect).
  const handleToggleLock = useCallback((sectionId, elementId, nextLocked) => {
    setContent(prev => {
      const sectionData = { ...(prev[sectionId] || {}) };
      const existing = sectionData[elementId] || {};
      sectionData[elementId] = { ...existing, locked: !!nextLocked };
      return { ...prev, [sectionId]: sectionData };
    });
  }, [setContent]);

  const handleSelectElement = useCallback((sectionId, elementId) => {
    setSelectedElement({ sectionId, elementId });
    // Phase 1.9 — a plain click on an element resets the multi-select to just
    // this one (or clears it if elementId is null).
    setSelectionSet(elementId ? [{ sectionId, elementId }] : []);
    // Clear editing when selection changes
    setEditingElement(null);
  }, []);

  // Phase 1.9 — Shift+click adds/removes from the multi-select set.
  const handleToggleSelection = useCallback((sectionId, elementId) => {
    if (!sectionId || !elementId) return;
    setSelectionSet((prev) => {
      const exists = prev.some(
        (s) => s.sectionId === sectionId && s.elementId === elementId,
      );
      if (exists) {
        const next = prev.filter(
          (s) => !(s.sectionId === sectionId && s.elementId === elementId),
        );
        // If we deselected the primary, promote the last remaining to primary.
        setSelectedElement(
          next.length ? next[next.length - 1] : null,
        );
        return next;
      }
      const next = [...prev, { sectionId, elementId }];
      setSelectedElement({ sectionId, elementId });
      return next;
    });
    setEditingElement(null);
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedElement(null);
    setSelectionSet([]);
    setEditingElement(null);
  }, []);

  // Phase 1.9 — "Convert to photo row" applies a centered row layout to the
  // currently selected 2-3 image elements. Takes a snapshot first so the
  // operation is reversible from the History panel. Each photo remains
  // individually editable afterwards — no parentId/groupId is created.
  const [convertingPhotoRow, setConvertingPhotoRow] = useState(false);
  const handleConvertToPhotoRow = useCallback(
    async (sectionId, overrides) => {
      if (!sectionId || !Array.isArray(overrides) || overrides.length === 0) return;
      setConvertingPhotoRow(true);
      try {
        // 1) Snapshot first so the operation is reversible.
        try {
          await axios.post(
            `${API}/api/events/${eventId}/rsvp-page/snapshot`,
            { label: 'Before photo row conversion' },
            { headers: { Authorization: `Bearer ${token}` } },
          );
        } catch {
          // Non-fatal — the conversion can still proceed without the snapshot.
        }

        // 2) Apply each photo's new override locally. Merge with any prior
        //    override so other fields (text, src, etc.) are preserved.
        setContent((prev) => {
          const sectionData = { ...(prev[sectionId] || {}) };
          for (const ov of overrides) {
            const existing = sectionData[ov.id] || {};
            sectionData[ov.id] = {
              ...existing,
              role: ov.role,
              customStyle: { ...(existing.customStyle || {}), ...(ov.customStyle || {}) },
              layout: {
                ...(existing.layout || {}),
                ...(ov.layout || {}),
                mobile: {
                  ...(existing.layout?.mobile || {}),
                  ...(ov.layout?.mobile || {}),
                },
                desktop: {
                  ...(existing.layout?.desktop || {}),
                  ...(ov.layout?.desktop || {}),
                },
              },
            };
          }
          return { ...prev, [sectionId]: sectionData };
        });

        toast.success('Arranged as a photo row');
      } finally {
        setConvertingPhotoRow(false);
      }
    },
    [eventId, token, setContent],
  );

  // ── Inline text editing ──
  const [editingElement, setEditingElement] = useState(null);

  const handleEditElement = useCallback((sectionId, elementId) => {
    setEditingElement({ sectionId, elementId });
  }, []);

  const handleEditText = useCallback((sectionId, elementId, field, value) => {
    setContent(prev => {
      const sectionData = { ...(prev[sectionId] || {}) };
      const elements = [...(sectionData._elements || [])];
      const idx = elements.findIndex(e => e.id === elementId);
      if (idx >= 0) {
        elements[idx] = { ...elements[idx], [field]: value };
        sectionData._elements = elements;
      }
      return { ...prev, [sectionId]: sectionData };
    });
  }, [setContent]);

  const handleSave = useCallback(async (opts = {}) => {
    const silent = opts.silent === true;
    history.flush();
    if (silent) setAutoSaving(true); else setSaving(true);
    try {
      const sectionVisibility = {};
      const sectionOrder = [];
      const sectionVariants = {};
      // Serialize full section configs for dynamic sections
      const dynamicSections = sections.map(s => ({
        id: s.id, type: s.type, label: s.label, visible: s.visible,
        variant: s.variant, height: s.height, background: s.background,
        data: s.data, editableRules: s.editableRules,
        canvasYPosition: s.canvasYPosition,
        // Don't serialize elements — those live in content or template defaults
      }));
      sections.forEach(s => {
        sectionVisibility[s.id] = s.visible;
        sectionOrder.push(s.id);
        sectionVariants[s.id] = s.variant;
      });

      if (isTemplateMode) {
        // Phase 2.0.1 — write to the template document, NOT the event.
        await axios.put(
          `${API}/api/templates/${templateId}/content`,
          {
            v2_template_id: templateConfig.templateId,
            v2_content: content,
            v2_theme_overrides: themeOverrides,
            v2_settings_overrides: { sectionVisibility, sectionOrder, sectionVariants, dynamicSections, motionPreset },
            layout_version: rsvpPageInfo?.layout_version,
            layout_mode: rsvpPageInfo?.layout_mode,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.put(
          `${API}/api/events/${eventId}/rsvp-page`,
          {
            engine: 'v2',
            v2_template_id: templateConfig.templateId,
            v2_content: content,
            v2_theme_overrides: themeOverrides,
            v2_settings_overrides: { sectionVisibility, sectionOrder, sectionVariants, dynamicSections, motionPreset },
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      setDirty(false);
      setLastSavedAt(Date.now());
      if (!silent) toast.success(isTemplateMode ? 'Template saved' : 'Invitation saved');

      // ─── Render parity guard ─────────────────────────────────
      // Fire-and-forget post-save check: compares the editor canvas
      // DOM with an off-screen public render and flags any element
      // whose computed position drifts beyond 2% AND 8px. Never
      // blocks the save. Skips template-mode (no public URL) and
      // silent auto-saves (the designer isn't actively reviewing).
      if (!silent && !isTemplateMode && slug) {
        try {
          const editorRoot = document.querySelector('[data-testid="phone-preview"]');
          const editorSnap = snapshotCanvas(editorRoot || document);
          if (editorSnap.sectionCount > 0) {
            const url = buildPublicPreviewUrl({
              slug,
              isPublished: !!rsvpPageInfo?.is_published,
              previewToken: rsvpPageInfo?.preview_token,
            });
            // Run the public-render snapshot AFTER a short delay so
            // the backend has a moment to propagate the save (we
            // just persisted via PUT; render is read-only).
            setTimeout(async () => {
              const { snapshot: publicSnap } = await snapshotPublicRender({ url });
              if (publicSnap) {
                const result = compareSnapshots(editorSnap, publicSnap);
                if (!result.matched && result.issues.length > 0) {
                  setParityResult(result);
                } else if (result.matched && result.editorCount > 0) {
                  // Editor and public render match — surface this
                  // briefly so the designer knows the save round-trip
                  // is healthy. If their phone still looks different,
                  // the most likely cause is a stale cache (PWA / SW /
                  // mobile browser). The toast hints at the fix.
                  toast.success(
                    `Preview matches phone (${result.editorCount} elements). If your device still differs, pull-to-refresh or clear the page cache.`,
                    { duration: 5000 }
                  );
                }
              }
            }, 400);
          }
        } catch (_) {
          /* parity guard is best-effort — never throw to the user */
        }
      }
    } catch (err) {
      if (!silent) toast.error('Save failed');
    } finally {
      if (silent) setAutoSaving(false); else setSaving(false);
    }
  }, [history, sections, content, themeOverrides, templateConfig, eventId, token, isTemplateMode, templateId, rsvpPageInfo, motionPreset]);

  // Keep ref synced so the auto-save effect always invokes the latest closure
  handleSaveRef.current = handleSave;

  const handlePublish = async () => {
    setPublishing(true);
    try {
      // Save first
      await handleSave();
      const res = await axios.post(
        `${API}/api/events/${eventId}/rsvp-page/publish`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIsPublished(true);
      toast.success('Invitation published! Guests can now view it.');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.errors) {
        detail.errors.forEach(e => toast.error(e));
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Publish failed');
      }
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublish = async () => {
    setPublishing(true);
    try {
      await axios.post(
        `${API}/api/events/${eventId}/rsvp-page/unpublish`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIsPublished(false);
      toast.success('Invitation unpublished');
    } catch (err) {
      toast.error('Failed to unpublish');
    } finally {
      setPublishing(false);
    }
  };

  const handleSharePreview = async () => {
    try {
      await handleSave();
      const res = await axios.post(
        `${API}/api/events/${eventId}/rsvp-page/preview-link`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Append cache-buster — guarantees the iPhone / mobile browser
      // refetches the page after a Save. The PWA service worker has
      // bitten us multiple times where the device stayed on a stale
      // snapshot for minutes after a real save round-tripped fine.
      const baseUrl = `${window.location.origin}${res.data.preview_url}`;
      const sep = baseUrl.indexOf('?') >= 0 ? '&' : '?';
      const fullUrl = `${baseUrl}${sep}v=${Date.now()}`;
      setPreviewUrl(fullUrl);
      setCopiedLink(true);
      try { await navigator.clipboard.writeText(fullUrl); } catch (e) { /* clipboard may be blocked */ }
      toast.success('Preview link ready! Share it with anyone.');
      setTimeout(() => setCopiedLink(false), 3000);
    } catch (err) {
      console.error('Preview link error:', err);
      toast.error('Failed to generate preview link');
    }
  };

  if (loading || !templateConfig) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="w-6 h-6 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Build preview-ready config with merged content
  const previewConfig = {
    ...templateConfig,
    sections: sections,
  };

  // Resolve image URLs for preview
  const resolvedContent = {};
  for (const [sid, sdata] of Object.entries(content)) {
    const resolved = { ...sdata };
    for (const [k, v] of Object.entries(resolved)) {
      if (k === '_elements' && Array.isArray(v)) {
        // Resolve src URLs inside _elements array
        resolved[k] = v.map(el => {
          if (el.src && typeof el.src === 'string' && el.src.startsWith('/api/')) {
            return { ...el, src: `${API}${el.src}` };
          }
          return el;
        });
      } else if (typeof v === 'string' && v.startsWith('/api/')) {
        resolved[k] = `${API}${v}`;
      } else if (Array.isArray(v)) {
        resolved[k] = v.map(item => typeof item === 'string' && item.startsWith('/api/') ? `${API}${item}` : item);
      } else if (v && typeof v === 'object' && !Array.isArray(v)) {
        const innerResolved = { ...v };
        for (const [ik, iv] of Object.entries(innerResolved)) {
          if (typeof iv === 'string' && iv.startsWith('/api/')) {
            innerResolved[ik] = `${API}${iv}`;
          }
        }
        resolved[k] = innerResolved;
      }
    }
    resolvedContent[sid] = resolved;
  }

  return (
    <OpenerDraftProvider initialOpener={openerConfig} onSave={handleSaveOpener}>
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex flex-col">
      {/* Top bar */}
      <div className="sticky top-0 z-40 bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-[1400px] mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(isTemplateMode ? '/studio/templates' : `/events/${eventId}/template-picker`)}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              data-testid="editor-back-btn"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
            <div>
              {isTemplateMode ? (
                <>
                  <h1
                    className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2"
                    data-testid="template-editor-heading"
                  >
                    Editing template: {initialTemplate?.name || '…'}
                    <span
                      className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-medium bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-200"
                      data-testid="template-mode-badge"
                    >
                      Template mode
                    </span>
                  </h1>
                  <p className="text-xs text-gray-500">{initialTemplate?.description || templateConfig.description}</p>
                </>
              ) : (
                <>
                  <h1 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{templateConfig.name}</h1>
                  <p className="text-xs text-gray-500">{templateConfig.description}</p>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Undo / Redo */}
            <div className="flex items-center gap-0.5 mr-2 border-r border-gray-200 dark:border-gray-700 pr-3">
              <button
                onClick={() => history.undo()}
                disabled={!history.canUndo}
                className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Undo (Ctrl+Z)"
                data-testid="undo-btn"
              >
                <Undo2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => history.redo()}
                disabled={!history.canRedo}
                className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Redo (Ctrl+Shift+Z)"
                data-testid="redo-btn"
              >
                <Redo2 className="w-4 h-4" />
              </button>
            </div>

            {slug && !isTemplateMode && (
              <a
                href={`/invite/${slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-1.5"
                data-testid="see-full-page-btn"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Full Page
              </a>
            )}
            {isTemplateMode && (
              <button
                onClick={() => window.open(`/studio/templates/${templateId}/preview`, '_blank', 'noopener,noreferrer')}
                className="px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-1.5"
                data-testid="template-preview-btn"
                title="Open the read-only template preview in a new tab"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Preview
              </button>
            )}
            {!isTemplateMode && (() => {
              return (
                <button
                  onClick={() => setShowLanguagesPanel(true)}
                  className="relative px-3 py-1.5 rounded-lg text-xs font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30 flex items-center gap-1.5 transition-colors border border-amber-200 dark:border-amber-800"
                  data-testid="header-languages-btn"
                  title={hasStaleLanguage
                    ? "A translation may be outdated. Open Languages to re-translate."
                    : "Make your invitation multilingual"}
                >
                  <Globe className="w-3.5 h-3.5" />
                  {(eventLanguages || []).length > 1 ? `${eventLanguages.length} languages` : 'Languages'}
                  {hasStaleLanguage && (
                    <span
                      className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-amber-500 ring-2 ring-white dark:ring-slate-900"
                      aria-label="Translation needs update"
                      data-testid="header-languages-stale-dot"
                    />
                  )}
                </button>
              );
            })()}
            {!isTemplateMode && (
              <button
                onClick={handleSharePreview}
                className="px-3 py-1.5 rounded-lg text-xs font-medium text-violet-700 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/20 hover:bg-violet-100 dark:hover:bg-violet-900/30 flex items-center gap-1.5 transition-colors border border-violet-200 dark:border-violet-800"
                data-testid="share-preview-btn"
                title={previewUrl || 'Generate a shareable preview link'}
              >
                {copiedLink ? <Check className="w-3.5 h-3.5" /> : <Link2 className="w-3.5 h-3.5" />}
                {copiedLink ? 'Copied!' : 'Share Preview'}
              </button>
            )}
            {slug && !isTemplateMode && (
              <button
                onClick={async () => {
                  const url = `${window.location.origin}/invite/${slug}?showcase=true`;
                  try { await navigator.clipboard.writeText(url); } catch {}
                  setCopiedShowcase(true);
                  toast.success('Showcase link copied! Open on phone & screen record.');
                  setTimeout(() => setCopiedShowcase(false), 3000);
                }}
                className="px-3 py-1.5 rounded-lg text-xs font-medium text-cyan-700 dark:text-cyan-400 bg-cyan-50 dark:bg-cyan-900/20 hover:bg-cyan-100 dark:hover:bg-cyan-900/30 flex items-center gap-1.5 transition-colors border border-cyan-200 dark:border-cyan-800"
                data-testid="copy-showcase-link-btn"
                title="Copy auto-scroll link for screen recording"
              >
                {copiedShowcase ? <Check className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                {copiedShowcase ? 'Copied!' : 'Showcase'}
              </button>
            )}
            {user?.is_admin && !isTemplateMode && (
              <button
                onClick={() => setShowSaveTemplateModal(true)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-900/20 hover:bg-violet-100 dark:hover:bg-violet-900/30 border border-violet-200 dark:border-violet-800/50 flex items-center gap-1.5 transition-colors"
                data-testid="save-as-template-btn"
                title="Admin · Save this design as a reusable template"
              >
                <Sparkles className="w-3.5 h-3.5" /> Save as Template
              </button>
            )}
            <SaveStatusIndicator dirty={dirty} autoSaving={autoSaving} saving={saving} lastSavedAt={lastSavedAt} />
            <button
              onClick={() => handleSave()}
              disabled={saving || autoSaving}
              className={`px-4 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors ${
                dirty
                  ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 hover:bg-amber-200 dark:hover:bg-amber-900/50 border border-amber-300 dark:border-amber-700/50'
                  : 'bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-700'
              } disabled:opacity-50`}
              data-testid="save-btn"
            >
              <Save className="w-3.5 h-3.5" /> {saving ? 'Saving...' : (isTemplateMode ? 'Save Template' : 'Save Draft')}
            </button>
            {!isTemplateMode && (isPublished ? (
              <button
                onClick={handleUnpublish}
                disabled={publishing}
                className="px-4 py-1.5 rounded-lg text-xs font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800 hover:bg-amber-100 disabled:opacity-50 flex items-center gap-1.5 transition-colors"
                data-testid="unpublish-btn"
              >
                <Eye className="w-3.5 h-3.5" /> {publishing ? '...' : 'Unpublish'}
              </button>
            ) : (
              <button
                onClick={handlePublish}
                disabled={publishing}
                className="px-4 py-1.5 rounded-lg text-xs font-medium bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 flex items-center gap-1.5 transition-colors"
                data-testid="publish-btn"
              >
                <Eye className="w-3.5 h-3.5" /> {publishing ? 'Publishing...' : 'Publish'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Editor body */}
      <div className="flex-1 flex max-w-[1400px] mx-auto w-full">
        {/* Left: Section editors */}
        <div className="w-[420px] flex-shrink-0 border-r border-gray-200 dark:border-gray-800 overflow-y-auto p-4 space-y-3" style={{ maxHeight: 'calc(100vh - 57px)' }}>
          {templateConfig.isStudio ? (
            /* ─── Studio Freeform Editor ─── */
            <>
              <StudioToolbar
                canvasSectionId={sections.find(s => s.type === 'collage')?.id || 'canvas-main'}
                onAddElement={handleCollageAdd}
                onAddPhotoRow={handleAddPhotoRow}
                onAddWidget={handleAddSection}
                onAddShape={handleAddShape}
                onAddOrnament={handleAddOrnament}
                onUploadOrnament={handleUploadOrnament}
                bgColor={sections.find(s => s.type === 'collage')?.background?.color || '#ffffff'}
                onBgColorChange={handleStudioBgColor}
                globalBg={themeOverrides.globalBg ? (themeOverrides.globalBg.startsWith('/') ? `${API}${themeOverrides.globalBg}` : themeOverrides.globalBg) : null}
                onGlobalBgUpload={handleGlobalBgUpload}
                onGlobalBgRemove={handleGlobalBgRemove}
                globalBgMeta={globalBgMeta}
                uploadingBg={uploadingGlobalBg}
                gridEnabled={gridEnabled}
                onToggleGrid={() => setGridEnabled(v => !v)}
                onOpenLanguages={() => setShowLanguagesPanel(true)}
                hasStaleLanguage={hasStaleLanguage}
                languagesCount={(eventLanguages || []).length}
                bgScroll={themeOverrides.bgScroll || 'fixed'}
                onBgScrollChange={(mode) => setThemeOverrides(prev => ({ ...prev, bgScroll: mode }))}
                bgFit={themeOverrides.bgFit || 'cover'}
                onBgFitChange={(mode) => setThemeOverrides(prev => ({ ...prev, bgFit: mode }))}
                rsvpPageInfo={rsvpPageInfo}
                eventId={eventId}
                onLayoutVersionChange={(v) => setRsvpPageInfo((prev) => ({ ...prev, layout_version: v }))}
                onSelectElement={handleSelectElement}
                onSnapToEdges={(sectionId, elementId) => {
                  // Phase 1.8.x — Mobile Layout Check one-click normalize.
                  // Re-uses handleCollageElementResize at full width which
                  // already auto-centers (x=0.5, xPercent=50) → renders as
                  // left 0% / width 100%. No new layout math here.
                  handleCollageElementResize(sectionId, elementId, 1.0);
                  toast.success('Snapped to canvas edges');
                }}
                guidesEnabled={guidesEnabled}
                onToggleGuides={toggleGuides}
              />

              {/* Phase 1.9 — History (snapshots). Hidden in template mode
                  (Phase 2.0.1) — use Duplicate template as the recovery path. */}
              {!isTemplateMode && (
                <SnapshotsPanel
                  eventId={eventId}
                  token={token}
                  onRestored={refetchInvitation}
                />
              )}

              {/* Page Flow — shows all sections in order, allows reorder */}
              <StudioPageFlow
                sections={sections}
                onReorder={handleMoveSection}
                onRemove={handleRemoveSection}
                onUpdateYPosition={handleUpdateCanvasYPosition}
              />

              {/* Widget Section Editors — for non-collage sections */}
              {sections.filter(s => s.type !== 'collage' && s.visible).map((section) => {
                const fullIdx = sections.findIndex(s => s.id === section.id);
                // Hero sections get the full SectionPanel (variant picker, image upload, CTA)
                if (section.type === 'hero') {
                  const sectionData = { ...(section.data || {}), ...(content[section.id] || {}) };
                  return (
                    <SectionPanel
                      key={section.id}
                      section={section}
                      sectionData={sectionData}
                      theme={templateConfig.theme}
                      onDataChange={handleDataChange}
                      onToggle={handleToggle}
                      onVariantChange={handleVariantChange}
                      onImageUpload={handleImageUpload}
                      uploadingField={uploadingField}
                      slug={slug}
                      dragHandleProps={{}}
                      allSections={sections}
                    />
                  );
                }
                return (
                  <WidgetSectionEditor
                    key={section.id}
                    section={section}
                    onUpdateData={handleUpdateSectionData}
                    onImageUpload={handleImageUpload}
                    uploadingField={uploadingField}
                    variants={SECTION_VARIANTS[section.type]}
                    onVariantChange={handleVariantChange}
                    onMoveUp={fullIdx > 0 ? () => handleMoveSectionById(section.id, 'up') : null}
                    onMoveDown={fullIdx < sections.length - 1 ? () => handleMoveSectionById(section.id, 'down') : null}
                    onRemove={() => handleRemoveSection(section.id)}
                    eventId={eventId}
                    token={token}
                  />
                );
              })}

              {/* Section & Widget Style — affects Hero + widgets only */}
              {sections.some(s => s.type !== 'collage' && s.visible) && (
                <CollapsiblePanel title="Section & Widget Style" icon={Palette} defaultOpen={false} testId="panel-studio-section-style">
                  <div className="space-y-4">
                    {/* Font Pair */}
                    <div>
                      <label className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 block">Font</label>
                      <div className="space-y-1.5">
                        {Object.values(fontPairs).map(fp => (
                          <button
                            key={fp.id}
                            onClick={() => setThemeOverrides(prev => ({ ...prev, fontPair: fp.id }))}
                            className={`w-full px-3 py-2 rounded-lg border-2 text-left transition-all ${
                              (themeOverrides.fontPair || templateConfig.theme.fontPair) === fp.id
                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                            }`}
                            data-testid={`studio-font-${fp.id}`}
                          >
                            <div className="text-sm font-medium text-gray-800 dark:text-gray-200" style={{ fontFamily: fp.heading }}>{fp.name}</div>
                            <div className="text-[10px] text-gray-400 mt-0.5" style={{ fontFamily: fp.body }}>Heading + Body</div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Color Palette */}
                    <div>
                      <label className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 block">Color Palette</label>
                      <div className="grid grid-cols-3 gap-2">
                        {Object.values(palettes).filter(p => !['warm-story', 'noir-gold', 'warm-paper'].includes(p.id)).map(pal => (
                          <button
                            key={pal.id}
                            onClick={() => setThemeOverrides(prev => ({ ...prev, palette: pal.id, colorOverrides: undefined }))}
                            className={`p-2 rounded-lg border-2 transition-all ${
                              (themeOverrides.palette || templateConfig.theme.palette) === pal.id && !themeOverrides.colorOverrides
                                ? 'border-blue-500'
                                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                            }`}
                            data-testid={`studio-palette-${pal.id}`}
                          >
                            <div className="flex gap-1 mb-1">
                              {[pal.bg, pal.accent, pal.textPrimary].map((c, i) => (
                                <div key={i} className="w-3 h-3 rounded-full border border-black/10" style={{ background: c }} />
                              ))}
                            </div>
                            <div className="text-[10px] text-gray-600 dark:text-gray-400 truncate">{pal.name}</div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Fine-tune Colors */}
                    <div>
                      <label className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 block">Fine-tune Colors</label>
                      <div className="space-y-2">
                        {[
                          { key: 'textPrimary', label: 'Text' },
                          { key: 'accent', label: 'Accent' },
                          { key: 'buttonBg', label: 'Button Background' },
                          { key: 'buttonText', label: 'Button Text' },
                        ].map(({ key, label }) => {
                          const activePalette = palettes[themeOverrides.palette || templateConfig.theme.palette] || palettes['ivory-gold'];
                          const currentVal = themeOverrides.colorOverrides?.[key] || activePalette[key];
                          return (
                            <div key={key} className="flex items-center gap-2">
                              <input
                                type="color"
                                value={currentVal}
                                onChange={e => setThemeOverrides(prev => ({
                                  ...prev,
                                  colorOverrides: { ...(prev.colorOverrides || {}), [key]: e.target.value }
                                }))}
                                className="w-7 h-7 rounded cursor-pointer border border-gray-200 dark:border-gray-700 p-0.5"
                                data-testid={`studio-color-${key}`}
                              />
                              <span className="text-xs text-gray-600 dark:text-gray-400">{label}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </CollapsiblePanel>
              )}

              {/* Phase 1.9 — Multi-selection bar (replaces single-element
                   toolbar when 2+ elements are selected via Shift+click). */}
              {selectionSet.length >= 2 && (() => {
                // Build a sectionId → mergedElements map so the bar can
                // resolve the selected elements regardless of section.
                const byId = {};
                for (const s of sections) {
                  if (s.type !== 'collage') continue;
                  const sectionContent = content[s.id] || {};
                  const elements = getElementList(s.id);
                  byId[s.id] = elements.map((e) => {
                    const ov = sectionContent[e.id] || {};
                    return {
                      ...e,
                      ...ov,
                      layout: {
                        ...(e.layout || {}),
                        ...(ov.layout || {}),
                        mobile: { ...(e.layout?.mobile || {}), ...(ov.layout?.mobile || {}) },
                        desktop: { ...(e.layout?.desktop || {}), ...(ov.layout?.desktop || {}) },
                      },
                    };
                  });
                }
                return (
                  <MultiSelectionBar
                    selectionSet={selectionSet}
                    elementsBySectionId={byId}
                    onClear={handleClearSelection}
                    onConvertToPhotoRow={handleConvertToPhotoRow}
                    busy={convertingPhotoRow}
                  />
                );
              })()}

              {/* Element Style Panel — shown when a text element is selected */}
              {selectedElement && selectionSet.length < 2 && (() => {
                const elements = getElementList(selectedElement.sectionId);
                const sectionContent = content[selectedElement.sectionId] || {};
                const el = elements.find(e => e.id === selectedElement.elementId);
                const overrides = sectionContent[selectedElement.elementId] || {};
                // Merge elements with their overrides so layer controls see the live z values
                const mergedElements = elements.map(e => {
                  const ov = sectionContent[e.id] || {};
                  return {
                    ...e,
                    ...ov,
                    layout: {
                      ...(e.layout || {}),
                      ...(ov.layout || {}),
                      mobile: { ...(e.layout?.mobile || {}), ...(ov.layout?.mobile || {}) },
                      desktop: { ...(e.layout?.desktop || {}), ...(ov.layout?.desktop || {}) },
                    },
                  };
                });
                const mergedSelected = mergedElements.find(e => e.id === selectedElement.elementId);
                if (el) {
                  return (
                    <>
                      <LayerOrderControls
                        sectionId={selectedElement.sectionId}
                        element={mergedSelected}
                        allElements={mergedElements}
                        onLayerUpdate={handleLayerZUpdate}
                      />
                      <AlignmentToolbar
                        element={mergedSelected}
                        sectionId={selectedElement.sectionId}
                        allElements={mergedElements}
                        onUpdate={handleCollageElementUpdate}
                        onAdd={handleCollageAdd}
                        onToggleLock={handleToggleLock}
                      />
                      <CollapsiblePanel title="Element Style" icon={Paintbrush} defaultOpen testId="panel-studio-element-style">
                        <ElementStylePanel
                          element={el}
                          overrides={overrides}
                          onUpdate={handleElementStyleUpdate}
                          onResize={handleCollageElementResize}
                          sectionId={selectedElement.sectionId}
                          eventId={eventId}
                          slug={slug}
                          previewToken={rsvpPageInfo?.preview_token}
                        />
                      </CollapsiblePanel>
                    </>
                  );
                }
                return null;
              })()}

              {/* Canvas height slider — covers single-screen mobile cards
                  up to long editorial scroll experiences (~9 phone screens
                  at 8000px) that pair with the `full_page_background`
                  optimizer profile. 100px steps so designers snap to round
                  page lengths. */}
              <CollapsiblePanel title="Canvas & Layout" icon={SlidersHorizontal} defaultOpen={false} testId="panel-studio-canvas">
                {(() => {
                  const canvasH = sections.find(s => s.type === 'collage')?.height?.mobile || 1600;
                  const VP = 844; // iPhone std logical viewport
                  const screens = (canvasH / VP).toFixed(1);
                  return (
                    <div
                      className="mb-2 px-2 py-1.5 rounded-md bg-indigo-50 dark:bg-indigo-900/20 text-[11px] font-mono leading-snug text-indigo-700 dark:text-indigo-300"
                      data-testid="studio-canvas-height-readout"
                    >
                      <div>Canvas height: <b>{canvasH}px</b></div>
                      <div className="opacity-80">Viewport: {VP}px · ≈ <b>{screens}</b> screen{screens === '1.0' ? '' : 's'}</div>
                    </div>
                  );
                })()}
                <input
                  type="range"
                  min={600}
                  max={8000}
                  step={100}
                  value={sections.find(s => s.type === 'collage')?.height?.mobile || 1600}
                  onChange={e => {
                    const h = parseInt(e.target.value);
                    setSections(prev => prev.map(s =>
                      s.type === 'collage' ? { ...s, height: { mobile: h, desktop: h } } : s
                    ));
                  }}
                  className="w-full accent-blue-500"
                  data-testid="studio-canvas-height"
                />
                <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                  <span>600px</span>
                  <span>{sections.find(s => s.type === 'collage')?.height?.mobile || 1600}px</span>
                  <span>8000px</span>
                </div>
              </CollapsiblePanel>

              {/* Phase 1.9 — Opening Animation (Rain on Cotton Paper) */}
              <CollapsiblePanel title="Opening Animation" icon={Sparkles} defaultOpen={false} testId="panel-studio-opener">
                <OpenerEditorPanel eventId={eventId} />
              </CollapsiblePanel>
              {/* Phase 1.9.19 — Entry Cover panel mounted via an external
                  host so the JSX nesting stays out of this 3000+ line
                  file's babel-traversal envelope. */}
              <CollapsiblePanel title="Entry Cover" icon={Sparkles} defaultOpen={false} testId="panel-studio-entry-cover">
                <React.Suspense fallback={<div className="p-3 text-[11px] text-gray-400">Loading…</div>}>
                  <EntryCoverPanelHost
                    eventId={eventId}
                    initialEntryCover={entryCoverConfig}
                    onSave={handleSaveEntryCover}
                    previewUrl={previewUrl}
                  />
                </React.Suspense>
              </CollapsiblePanel>
              <button
                onClick={() => {
                  setAnimationPreview(true);
                  setTimeout(() => setAnimationPreview(false), 4000);
                }}
                disabled={animationPreview}
                className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-medium transition-all ${
                  animationPreview
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
                data-testid="preview-animations-btn"
              >
                <Play className="w-3.5 h-3.5" />
                {animationPreview ? 'Playing...' : 'Preview Animations'}
              </button>

              {/* Layers */}
              <CollapsiblePanel title="Layers & Elements" icon={Layers} defaultOpen={false} testId="panel-studio-layers">
                <div className="flex items-center gap-2 mb-2">
                  <button
                    onClick={() => setShowLayers(v => !v)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      showLayers
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                    data-testid="studio-toggle-layers-btn"
                  >
                    <Layers className="w-3.5 h-3.5" />
                    {showLayers ? 'Layers' : 'Elements'}
                  </button>
                </div>

                {showLayers ? (
                  <LayerPanel
                    sections={sections}
                    content={content}
                    selectedElement={selectedElement}
                    selectionSet={selectionSet}
                    onSelect={handleSelectElement}
                    onToggleSelection={handleToggleSelection}
                    onUpdateLayout={handleLayerZUpdate}
                    onToggleLock={handleToggleLock}
                  />
                ) : (
                  <CollageEditor
                    sections={sections.filter(s => s.type === 'collage')}
                    content={content}
                    onUpdate={handleCollageElementUpdate}
                    onToggle={handleToggle}
                    onImageUpload={handleCollageImageUpload}
                    uploadingField={uploadingField}
                    selectedElement={selectedElement}
                    onSelect={handleSelectElement}
                    onDuplicate={handleCollageDuplicate}
                    onRemove={handleCollageRemove}
                    onAdd={handleCollageAdd}
                    onSavePreset={handleSavePreset}
                    onAutoBalance={handleAutoBalance}
                    token={token}
                  />
                )}
              </CollapsiblePanel>

              {/* Motion 1.0 — Soft Reveal global toggle. Collapsed by default. */}
              <MotionPanel
                motionPreset={motionPreset}
                onChange={(v) => { setMotionPreset(v); setDirty(true); }}
              />
            </>
          ) : templateConfig.isCollage ? (
            /* ─── Collage Editor with Section Manager ─── */
            <>
              {/* Section Manager */}
              <CollapsiblePanel title="Sections" icon={Layers} defaultOpen testId="panel-collage-sections">
                <SectionManager
                  sections={sections}
                  onAddSection={handleAddSection}
                  onRemoveSection={handleRemoveSection}
                  onMoveSection={handleMoveSectionById}
                  onUpdateSection={handleUpdateSection}
                  onBgUpload={handleSectionBgUpload}
                  uploadingSectionId={uploadingSectionBg}
                />
              </CollapsiblePanel>

              {/* Motion 1.0 — Soft Reveal global toggle. Collapsed by default. */}
              <MotionPanel
                motionPreset={motionPreset}
                onChange={(v) => { setMotionPreset(v); setDirty(true); }}
              />

              {/* Global Background Image */}
              <CollapsiblePanel title="Background Image" icon={ImageIcon} defaultOpen={false} testId="panel-collage-bg">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex-shrink-0 overflow-hidden flex items-center justify-center">
                    {themeOverrides.globalBg ? (
                      <img src={themeOverrides.globalBg.startsWith('/') ? `${API}${themeOverrides.globalBg}` : themeOverrides.globalBg} alt="background" className="w-full h-full object-cover" />
                    ) : (
                      <ImageIcon className="w-4 h-4 text-gray-300" />
                    )}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label
                      className={`px-3 py-1.5 rounded-lg text-[11px] font-medium cursor-pointer transition-colors text-center ${
                        uploadingGlobalBg ? 'bg-gray-100 text-gray-400' : 'bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400'
                      }`}
                      data-testid="upload-global-bg-btn"
                    >
                      {uploadingGlobalBg ? 'Uploading...' : themeOverrides.globalBg ? 'Replace' : 'Upload'}
                      <input type="file" accept="image/*" className="hidden" disabled={uploadingGlobalBg} onChange={e => {
                        const f = e.target.files?.[0];
                        if (f) handleGlobalBgUpload(f);
                        e.target.value = '';
                      }} />
                    </label>
                    {themeOverrides.globalBg && (
                      <button
                        onClick={handleGlobalBgRemove}
                        className="px-2 py-1 rounded-lg text-[11px] text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                        data-testid="remove-global-bg-btn"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-2">Image, texture, or pattern behind all sections</p>

                {/* Background scroll mode — only when an image is set */}
                {themeOverrides.globalBg && (
                  <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
                    <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5 block">
                      Scroll behavior
                    </label>
                    <div className="grid grid-cols-2 gap-1.5">
                      <button
                        onClick={() => setThemeOverrides(prev => ({ ...prev, bgScroll: 'fixed' }))}
                        className={`px-2 py-2 rounded-lg text-[11px] font-medium transition-colors ${
                          (themeOverrides.bgScroll || 'fixed') === 'fixed'
                            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800'
                            : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 border border-transparent'
                        }`}
                        data-testid="bg-scroll-fixed"
                      >
                        Fixed
                        <span className="block text-[9px] font-normal opacity-70 mt-0.5">Stays in place</span>
                      </button>
                      <button
                        onClick={() => setThemeOverrides(prev => ({ ...prev, bgScroll: 'scroll' }))}
                        className={`px-2 py-2 rounded-lg text-[11px] font-medium transition-colors ${
                          themeOverrides.bgScroll === 'scroll'
                            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800'
                            : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 border border-transparent'
                        }`}
                        data-testid="bg-scroll-scrolls"
                      >
                        Scrolls
                        <span className="block text-[9px] font-normal opacity-70 mt-0.5">Moves with content</span>
                      </button>
                    </div>
                  </div>
                )}
              </CollapsiblePanel>

              {/* Widget Section Editors — for non-collage sections */}
              {sections.filter(s => s.type !== 'collage' && s.visible).map((section, idx, arr) => (
                <WidgetSectionEditor
                  key={section.id}
                  section={section}
                  onUpdateData={handleUpdateSectionData}
                  variants={SECTION_VARIANTS[section.type]}
                  onVariantChange={handleVariantChange}
                  onMoveUp={idx > 0 ? () => handleMoveSectionById(section.id, 'up') : null}
                  onMoveDown={idx < arr.length - 1 ? () => handleMoveSectionById(section.id, 'down') : null}
                  onRemove={() => handleRemoveSection(section.id)}
                />
              ))}

              {/* Element Style Panel — shown when a text element is selected */}
              {selectedElement && (() => {
                const sec = sections.find(s => s.id === selectedElement.sectionId);
                const elements = getElementList(selectedElement.sectionId);
                const el = elements.find(e => e.id === selectedElement.elementId);
                const overrides = content[selectedElement.sectionId]?.[selectedElement.elementId] || {};
                if (el && sec?.type === 'collage') {
                  return (
                    <CollapsiblePanel title="Element Style" icon={Paintbrush} defaultOpen testId="panel-collage-element-style">
                      <ElementStylePanel
                        element={el}
                        overrides={overrides}
                        onUpdate={handleElementStyleUpdate}
                        onResize={handleCollageElementResize}
                        sectionId={selectedElement.sectionId}
                        eventId={eventId}
                        slug={slug}
                        previewToken={rsvpPageInfo?.preview_token}
                      />
                    </CollapsiblePanel>
                  );
                }
                return null;
              })()}

              {/* Quick Style Swap */}
              <CollapsiblePanel title="Quick Style" icon={Palette} defaultOpen={false} testId="panel-collage-style-swap">
                <StyleSwapBar
                  currentPresetId={activeStylePreset || 'warm-editorial'}
                  onSelect={handleStylePresetSelect}
                />
              </CollapsiblePanel>

              {/* Layers / Elements */}
              <CollapsiblePanel title="Layers & Elements" icon={Layers} defaultOpen={false} testId="panel-collage-layers">
                <div className="flex items-center gap-2 mb-2">
                  <button
                    onClick={() => setShowLayers(v => !v)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      showLayers
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                    data-testid="toggle-layers-btn"
                  >
                    <Layers className="w-3.5 h-3.5" />
                    {showLayers ? 'Layers' : 'Elements'}
                  </button>
                </div>

                {showLayers ? (
                  <LayerPanel
                    sections={sections}
                    content={content}
                    selectedElement={selectedElement}
                    selectionSet={selectionSet}
                    onSelect={handleSelectElement}
                    onToggleSelection={handleToggleSelection}
                    onUpdateLayout={handleLayerZUpdate}
                    onToggleLock={handleToggleLock}
                  />
                ) : (
                  <CollageEditor
                    sections={sections}
                    content={content}
                    onUpdate={handleCollageElementUpdate}
                    onToggle={handleToggle}
                    onImageUpload={handleCollageImageUpload}
                    uploadingField={uploadingField}
                    selectedElement={selectedElement}
                    onSelect={handleSelectElement}
                    onDuplicate={handleCollageDuplicate}
                    onRemove={handleCollageRemove}
                    onAdd={handleCollageAdd}
                    onSavePreset={handleSavePreset}
                    onAutoBalance={handleAutoBalance}
                    token={token}
                  />
                )}
              </CollapsiblePanel>
            </>
          ) : (
            /* ─── Standard Editor ─── */
            <>
              {/* Theme selector */}
              <CollapsiblePanel title="Color Palette" icon={Palette} defaultOpen={false} testId="panel-standard-palette">
                <div className="grid grid-cols-3 gap-2">
                  {Object.values(palettes).map(pal => (
                    <button
                      key={pal.id}
                      onClick={() => setThemeOverrides(prev => ({ ...prev, palette: pal.id }))}
                      className={`p-2 rounded-lg border-2 transition-all ${
                        (themeOverrides.palette || templateConfig.theme.palette) === pal.id
                          ? 'border-blue-500'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex gap-1 mb-1">
                        {[pal.bg, pal.accent, pal.textPrimary].map((c, i) => (
                          <div key={i} className="w-3 h-3 rounded-full" style={{ background: c }} />
                        ))}
                      </div>
                      <div className="text-[10px] text-gray-600 dark:text-gray-400 truncate">{pal.name}</div>
                    </button>
                  ))}
                </div>
              </CollapsiblePanel>

              {/* Font pair selector */}
              <CollapsiblePanel title="Font Style" icon={Paintbrush} defaultOpen={false} testId="panel-standard-fonts">
                <div className="space-y-2">
                  {Object.values(fontPairs).map(fp => (
                    <button
                      key={fp.id}
                      onClick={() => setThemeOverrides(prev => ({ ...prev, fontPair: fp.id }))}
                      className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                        (themeOverrides.fontPair || templateConfig.theme.fontPair) === fp.id
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-sm font-medium text-gray-800 dark:text-gray-200" style={{ fontFamily: fp.heading }}>{fp.name}</div>
                      <div className="text-xs text-gray-500 mt-0.5" style={{ fontFamily: fp.body }}>Heading + Body preview</div>
                    </button>
                  ))}
                </div>
              </CollapsiblePanel>

              {/* Section editors — drag reorderable */}
              <DraggableSectionList
                sections={sections}
                content={content}
                templateConfig={templateConfig}
                onDataChange={handleDataChange}
                onToggle={handleToggle}
                onVariantChange={handleVariantChange}
                onImageUpload={handleImageUpload}
                uploadingField={uploadingField}
                slug={slug}
                onReorder={handleMoveSection}
              />
            </>
          )}
        </div>

        {/* Right: Phone preview — clicking the empty area around the phone deselects */}
        <div
          className="flex-1 flex items-start justify-center p-8 overflow-y-auto"
          style={{ maxHeight: 'calc(100vh - 57px)' }}
          onMouseDown={(e) => {
            if (templateConfig.isCollage && e.target === e.currentTarget) {
              setSelectedElement(null);
            }
          }}
          data-testid="phone-preview-pane"
        >
          <div
            className="sticky top-8"
            onMouseDown={(e) => {
              if (templateConfig.isCollage && e.target === e.currentTarget) {
                setSelectedElement(null);
              }
            }}
          >
            <div
              className="rounded-[2.5rem] border-[8px] border-gray-800 dark:border-gray-700 overflow-hidden shadow-2xl"
              style={{
                width: 390,
                height: 844,
                // Create a containing block so `position: fixed` descendants
                // (e.g. the global background in TemplateRenderer) anchor to
                // this phone preview frame instead of the browser viewport.
                transform: 'translateZ(0)',
              }}
              data-testid="phone-preview"
            >
              {templateConfig.isCollage ? (
                /* Collage: inline rendering with edit context for drag/resize/rotate */
                <div
                  className="w-full h-full overflow-y-auto overflow-x-hidden"
                  style={{ WebkitOverflowScrolling: 'touch' }}
                  onMouseDown={(e) => {
                    // Deselect only when clicking directly on the scroll container background
                    if (e.target === e.currentTarget) setSelectedElement(null);
                  }}
                >
                  <CollageEditContext.Provider value={{
                    onElementMove: handleCollageElementMove,
                    onElementResize: handleCollageElementResize,
                    onElementRotate: handleCollageElementRotate,
                    onDeleteElement: handleDeleteElement,
                    onImageUpload: handleCanvasImageUpload,
                    selectedElement,
                    onSelectElement: handleSelectElement,
                    // Phase 1.9 — multi-select state surfaced to the canvas.
                    selectionSet,
                    onToggleSelection: handleToggleSelection,
                    onClearSelection: handleClearSelection,
                    editingElement,
                    onEditElement: handleEditElement,
                    onEditText: handleEditText,
                    layoutMode: 'mobile',
                    gridEnabled,
                    guidesEnabled,
                    animationPreview,
                  }}>
                    <TemplateRenderer
                      templateConfig={previewConfig}
                      invitationData={resolvedContent}
                      themeOverrides={themeOverrides}
                      settingsOverrides={{ motionPreset }}
                      slug={slug}
                    />
                  </CollageEditContext.Provider>
                </div>
              ) : (
                /* Section-based: iframe for correct mobile CSS media queries */
                <IframePreview
                  templateConfig={templateConfig}
                  sections={sections}
                  invitationData={resolvedContent}
                  themeOverrides={themeOverrides}
                  slug={slug}
                  selectedElement={selectedElement}
                />
              )}
              {/* Phase 1.9 — Opening Animation preview, inside the phone
                  mockup. Only renders when the opener is enabled AND the
                  panel has called Replay (replayKey > 0). Mounts above the
                  canvas with position:absolute inside the relative phone-
                  preview container. */}
              <OpenerPreviewOverlay />
            </div>
            <p className="text-center text-xs text-gray-400 mt-4">
              {templateConfig.isCollage ? 'Drag elements to reposition' : 'Live Preview (Mobile)'}
            </p>
          </div>
        </div>
      </div>
      <SaveAsTemplateModal
        open={showSaveTemplateModal}
        onClose={() => setShowSaveTemplateModal(false)}
        eventId={eventId}
        token={token}
      />
      {/* Render parity guard banner — non-blocking. Lets the
          designer dismiss, open details (which surfaces the drifted
          elements + lets them click-to-select), or jump straight
          into the Mobile Layout Check for a deeper view. */}
      <RenderParityBanner
        result={parityResult}
        onDismiss={() => setParityResult(null)}
        onSelectElement={(sectionId, elementId) => {
          setParityResult(null);
          handleSelectElement(sectionId, elementId);
        }}
      />
      {/* PWA / browser cache buster — surfaces a soft "newer version
          available" banner when the deployed bundle hash differs from
          the currently-running tab. Honors unsaved changes (refresh
          button disabled until the designer saves). */}
      <NewVersionBanner dirty={dirty} />
      {showLanguagesPanel && (
        <LanguagesPanel
          eventId={eventId}
          languages={eventLanguages}
          defaultLanguage={eventDefaultLanguage}
          hasTranslation={Object.fromEntries(Object.keys(v2Translations || {}).map((k) => [k, true]))}
          translationGeneratedAt={Object.fromEntries(
            Object.entries(v2Translations || {}).map(([k, v]) => [k, (v && v._generated_at) || null])
          )}
          contentUpdatedAt={rsvpPageInfo?.updated_at || null}
          onSaved={({ languages, default_language }) => {
            setEventLanguages(languages || []);
            setEventDefaultLanguage(default_language || 'en');
          }}
          onTranslated={async () => {
            // Refetch rsvp_page to pull updated v2_translations
            try {
              const res = await axios.get(`${API}/api/events/${eventId}/rsvp-page`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              setV2Translations(res.data?.v2_translations || {});
            } catch { /* */ }
          }}
          onClose={() => setShowLanguagesPanel(false)}
        />
      )}
    </div>
    </OpenerDraftProvider>
  );
}
